/**
 * 모바일 관리자 — 기사관리.
 * 데스크탑 /adm/members 와 같은 목록(Admin\MemberController@list)을 카드로 렌더하고,
 * 카드 탭 시 상세 시트에서 상태변경(대기/복구/탈퇴)·푸시테스트·상담을 처리한다.
 * 액션은 /ma/ajax/member/{n}/* → 데스크탑 정본 메서드에 그대로 위임한다.
 */
import { CAR_TYPE_BG, BONSA_CLS, scrollSecTop, reScrollSecTop } from './const';
import { openPenalty } from './penaltySheet';
import Util from '../../Common/Util';
import { saveListReturn, takeListReturn, restoreScroll } from './listReturn';

const LENGTH = 30;

const MEM_STATUS = { 0: '정상', 1: '대기', 98: '탈퇴신청', 99: '탈퇴' };

// 상담 유형별 줄 색 — PC($j.F.Admin.Counsel.types) 값 그대로.
// '경고'는 상담 유형이 아니라 제재에서 끼워 넣는 읽기전용 줄이다(src='penalty') → 별도 색.
const CNS_BG = {
    '상담': '#ffffff', '가입': '#fffce3', '추가가입': '#fcf6bd', '연장': '#f7ffde',
    '정지': '#e8eefa', '탈퇴': '#edf4fc', '중요': '#ffffff', '기타': '#ffffff',
};
const CNS_WARN_BG = '#ffc1cc';   // 제재 경고줄 (PC Counsel.WARN_BG)

/**
 * 기사 정보 편집 — 묶음별 필드 선언.
 *
 * 저장은 데스크탑 정본(POST /adm/ajax/member/v2/save)에 그대로 위임한다. 그 엔드포인트는
 * **보낸 키만 반영**하고 안 보낸 키는 DB 현재값으로 채우므로(MemberAdminSaveService::fillMissing),
 * 폰에서 묶음 하나만 보내도 나머지 값이 빈값으로 날아가지 않는다.
 *
 * `owner:true` 는 소유자 귀속(g5_member_owners·g5_company) 필드다. 소속자에게 보내면 서버가
 * 저장하지 않으므로(스코프 가드) 아예 화면에 내지 않는다 — 데스크탑 편집창과 같은 규칙이다.
 *
 * 여기 없는 필드는 셋 중 하나다 —
 *   · 전용 화면/엔드포인트가 따로 있다 : 아이디변경 · 상태변경(관리탭) · 차수 · 발주개수 · 회비 · 장비 · 포인트
 *   · 값만으로 안 끝난다              : 비밀번호 · 차고지주소(주소검색) · 계좌(통장사본 연동) · 소개기사(회원검색)
 *   · 신원·권한이라 폰에서 안 다룬다  : 기사타입 · 기사권한 · 소유자코드 · 소유자권한 · 소속(+추가소속)
 *
 * f: [라벨, name, 종류, 상세응답에서 현재값 경로, 조건?]
 *   종류 = text|tel|date|num|select
 *   조건 = 묶음 안 예외. 'owner' 소유자만 · 'sosok' 소속자만 · 'samsung' 소속이 삼성일 때만
 *          · 'car' 수발주자만 · 'permitfree' 수발주자가 **아닐** 때만.
 *          (데스크탑 편집창의 노출 조건과 같다. 안 맞는 칸을 내면 저장되지 않는 헛편집이 된다)
 */
const EDIT_GROUPS = [
    { key: 'status', label: '유형·상태', owner: false, f: [
        // 기사타입은 소유자 단위 설정이다 — 소속자 화면에서 바꾸면 형제 소속자와 어긋난다
        ['기사타입',   'mem_driver_type', 'select:수발주자=수발주자,발주자=발주자,업체발주자=업체발주자', 'sum.driverType', 'owner'],
        // 수발주자는 캐시·차수·장비가 소유자 단위로 묶여 소유자↔소속자 전환이 막혀 있다(데스크탑 permitLocked).
        // 소유권 이전은 데스크탑 '소속자로' 버튼이 담당한다.
        ['기사권한',   'mem_permit', 'select:소유자=소유자,소속자=소속자', 'sum.permit', 'permitfree'],
        // 탈퇴(99)는 넣지 않는다 — 되돌리기 어려운 처리라 확인절차가 붙은 「관리」탭 버튼으로만 한다
        ['서비스상태', 'mem_status', 'select:0=등록,1=대기,2=휴면,98=탈퇴신청', 'sum.status'],
        ['소유자권한', 'is_owner_auth_sel', 'select:1=ON,0=OFF', 'sosok.isOwnerAuth', 'sosok'],
        // 위치동의는 회원 컬럼이 아니라 법적 동의이력이다 — 값이 실제로 바뀔 때만 이력이 쌓인다
        ['위치동의',   'loc_consent', 'select:none=무응답,refuse=미동의,agree=동의', 'basic.locConsent', 'car'],
    ] },
    { key: 'basic', label: '기본정보', owner: false, f: [
        ['이름',     'mem_name',     'text', 'sum.name'],
        ['전화',     'mem_phone',    'tel',  'sum.phone'],
        ['생년월일', 'mem_birthday', 'date', 'basic.birthday'],
        // 비밀번호는 입력칸을 두지 않는다 — 아래 [비밀번호 리셋] 버튼 하나뿐이다(blockActs)
    ] },
    { key: 'contact', label: '연락·상태', owner: true, f: [
        ['추가폰',    'mem_phone2', 'tel',  'basic.phone2'],
        ['이메일',    'mem_email',  'text', 'basic.email'],
        ['통화여부',  'is_call',    'select:0=부재중,1=통화완료', 'basic.isCall'],
        // 마케팅도 동의이력이다 — 값이 실제로 바뀔 때만 ADMIN_PROXY 이력이 쌓인다
        ['마케팅',    'marketing_consent', 'select:none=무응답,refuse=미동의,agree=동의', 'basic.mkConsent'],
        ['테스트계정','is_test',    'select:0=NO,1=YES', 'basic.isTest'],
        // 이 달까지 회비 — 다음달 1일 배치가 C그룹(무료)으로 옮긴다 (MemberPayStopService)
        ['유료정지예정','pay_stop_ym', 'month', 'basic.payStopYm'],
        ['사원코드',  'samsung_emp_code', 'text', 'basic.empCode', 'samsung'],
    ] },
    // 대표소속은 소유자 귀속(g5_member_owners.sosok)이다 — 소속자는 소유자를 따라간다.
    // 3단(본사·지사·센터) 선택은 common.js 의 공용 헬퍼가 그리고, 확정값은 가장 하위 선택이다.
    { key: 'sosok', label: '소속', owner: true, f: [
        ['대표소속', 'sosok', 'sosok3', 'sum.sosokCode'],
    ] },
    { key: 'match', label: '보유장비', owner: false, f: [
        ['매칭반경(km)', 'mem_areakm', 'num', 'car.areakm'],
        ['수주승인',     'is_soojoo',  'select:1=YES,0=NO', 'car.isSoojoo', 'owner'],
        // GPS 는 소속자 설정이다 — 소유자는 늘 본인 위치라 데스크탑도 select 을 내지 않는다
        ['GPS',          'is_gps',     'select:0=켜기,1=끄기(관리폰),2=소유자위치 따라가기', 'car.isGps', 'sosok'],
        // 차고지는 좌표까지 함께 바뀌어야 한다(당일외 오더 거리계산 기준) — 지도에서 고른다
        ['차고지주소',   'car_parkaddress', 'addr', 'car.park'],
    ] },
    { key: 'company', label: '사업자', owner: true, f: [
        ['사업자번호', 'co_number',  'text', 'company.number'],
        ['상호',       'co_title',   'text', 'company.title'],
        ['대표자',     'co_ceoname', 'text', 'company.ceo'],
        ['대표생일',   'co_birth',   'date', 'company.birth'],
        ['업체전화',   'co_phone',   'tel',  'company.phone'],
        ['E-mail',     'user_email', 'text', 'company.email'],
        ['팩스',       'user_fax',   'tel',  'company.fax'],
        ['사업장주소', 'co_address', 'text', 'company.address'],
    ] },
];

/**
 * 읽기 블록 ↔ 편집묶음 — **고치는 자리는 보는 자리와 같아야 한다.**
 *
 * 머리에 [정보 수정] 하나를 두고 화면 전체를 편집폼으로 바꾸던 것을 접었다. 정보 탭이 열 블록
 * 남짓이라, 사업자 한 줄을 고치려고 화면을 통째로 갈아엎고 다시 찾아 내려가야 했다.
 * 이제 블록마다 제 [수정] 을 달고, 누른 블록만 그 자리에서 입력칸으로 바뀐다.
 *
 * 값이 여러 블록에 흩어진 묶음은 값이 가장 많이 사는 블록에 붙인다
 * (예: 유형·상태의 소유자권한은 '소속' 블록에 있다).
 */
const BLOCK_EDIT = {
    type:    ['status'],             // 기사타입·기사권한·서비스상태·소유자권한·위치동의
    sosok:   ['sosok'],              // 대표소속(본사·지사·센터)
    basic:   ['basic', 'contact'],   // 이름·전화·생일 + 추가폰·이메일·통화·마케팅·가입일·사원코드
    car:     ['match'],              // 매칭반경·수주승인·GPS
    company: ['company'],            // 사업자번호·상호·대표자·주소…
};
// 소속 블록에서 고칠 수 있는 것은 대표소속뿐이다. 추가소속·소유자연결·소개기사는 회원검색이
// 붙는 별도 창이라 데스크탑에 남긴다 — 읽기 줄로만 함께 보인다.

/**
 * 원장 내역 창의 재료 — 캐시·발주개수·수주포인트·-6차. 서버 응답 모양이 원장마다 달라
 * (캐시·포인트는 point/total_point, 발주개수는 diff/total, -6차는 grant 목록) 여기서 흡수한다.
 * 주소는 데스크탑과 같은 정본이고 /ma 로만 걸어둔 것이다 (routes/madmin.php).
 */
const LEDGER = {
    cash: {
        title: '보유캐시 내역', seg: 'cash',
        head: (d, m) => `가용 <b>${m.num(d.have)}</b>원`,
        row:  (r, m) => m.lgRow(r.date, r.label, r.memo || (r.relTable === 'order' && r.relId ? `오더 ${r.relId}` : ''), r.amount, r.total, '원'),
    },
    odrcnt: {
        title: '발주개수 내역', seg: 'odrcnt',
        head: (d, m) => `보유 <b>${m.num((d.owner || {}).mem_order_cnt)}</b>개 · 차수 <b>${m.num((d.owner || {}).mem_ordercounter)}</b>차`,
        row:  (r, m) => m.lgRow(r.date, r.label, r.order || '', r.diff, r.total, '개'),
    },
    points: {
        title: '수주포인트 내역', seg: 'points',
        head: (d, m) => `보유 <b>${m.num(d.have)}</b>P`,
        row:  (r, m) => m.lgRow(r.date, r.label, r.memo || (r.relTable === 'order' && r.relId ? `오더 ${r.relId}` : ''), r.point, r.total, 'P'),
    },
    // -6차는 페이지를 자르지 않는다 (서버가 최근 200건을 한 번에 준다)
    msix: {
        title: '-6차 내역', seg: 'msix', nopage: true,
        head: (d, m) => `보유 <b>${m.num((d.counts || {}).active)}</b>개 · 총 ${m.num((d.counts || {}).total)}개`,
        row:  (r, m) => m.lgRow(r.start_date, r.used_date ? `사용 (오더 ${r.used_ordernum || '-'})` : `${r.nday}일차 보유중`,
            r.ordernum ? `발주 ${r.ordernum}` : '수동지급', r.current_pt, r.initial_pt, 'pt'),
    },
};

/**
 * [저장] 을 눌러야 반영되는 블록 — 버튼이 값을 **화면에만 올려두고**(스테이징) 하단 저장바에서
 * 한 번에 보낸다. 계좌는 출금↔CMS 를 대조하며 두 칸을 함께 손보는 일이 잦아, 누를 때마다
 * 저장되면 중간 상태가 그대로 DB 에 남는다 (데스크탑도 [저장] 때 함께 보낸다).
 *
 * 여기 없는 액션 블록(회비·캐시·자원)은 버튼 하나가 곧 한 건의 처리(입출금·지급·재계산)라
 * 올려둘 값이 없다 — 하단은 [닫기] 하나만 선다.
 */
const STAGE_BLOCKS = { bank: 1 };

// 캐시 입출금 사유별 자동완성 — 데스크탑 별창(cashInout.blade)의 CI_PRESET 과 같은 값이다.
// fee:true 면 회비 년/월 줄을 쓴다 (memos 가 'YYYY년MM월' 라벨로 나가고 메모는 exmemos 로 간다).
const CASH_PRESET_LIST = {
    '충전금':                  { inorout: '입금', amount: '',      fee: false, memo: '' },
    '페널티':                  { inorout: '입금', amount: '',      fee: false, memo: '' },
    '페널티원복':              { inorout: '입금', amount: '',      fee: false, memo: '' },
    '수주':                    { inorout: '입금', amount: '',      fee: false, memo: '' },
    '발주':                    { inorout: '입금', amount: '',      fee: false, memo: '' },
    '프로그램사용료':          { inorout: '출금', amount: 44000,   fee: true,  memo: '{name}_회비 출금' },
    '프로그램사용료 (카드결제)': { inorout: '출금', amount: 0,       fee: true,  memo: '{name}_회비 장기선결제' },
    '추가등록비':              { inorout: '출금', amount: '',      fee: false, memo: '{name}_추가등록 ( * 톤) - 톤수입력필수' },
};
const CASH_PRESET_NONE = { inorout: '입금', amount: '', fee: false, memo: '' };

// GPS 설정 — 매칭 SQL 이 `is_gps in (0,2)` 로 거른다(끄기=1 은 매칭에서 빠진다)
const GPS_LABEL = { 0: '켜기', 1: '끄기(관리폰)', 2: '소유자위치 따라가기' };

// select 에서 고를 수 없는 현재값의 표시 이름 (상태 표시일 뿐 CS 가 직접 고르는 값이 아니다)
const ODD_VALUE = { stale: '재동의필요' };

// 위치정보 동의 상태 (수발주자만 값이 있다)
const LOC_LABEL = { none: '무응답', refuse: '미동의', agree: '동의', stale: '재동의필요' };

// 기사 상세 시트 탭 — 접수(오더 상세)와 같은 규약: 읽는 탭들 + 손대는 '관리' 탭 분리
const TABS = [
    { key: 'info',    label: '정보' },
    { key: 'counsel', label: '상담' },
    { key: 'log',     label: '기록' },
    { key: 'act',     label: '관리' },
];

export default class Member {
    initialize() {
        this.$list    = $('#maMemList');
        this.$more    = $('#maMemMore');
        this.$moreBtn = $('#maMemMoreBtn');
        this.$empty   = $('#maMemEmpty');
        this.$loading = $('#maMemLoading');
        this.$sum     = $('#maMemSum');
        this.$chips   = $('#maMemChips');

        this.state = { st: 'S0', start: 0, total: 0, loading: false };
        this.rows  = {};

        // 상세검색 — PC /adm/members 와 같은 파라미터(opt 글자·x1 소속·x3 유형·x5 미접속).
        // 접수와 달리 '아무것도 안 고르면 조건 없음' 이다 (PC 체크박스와 같은 뜻).
        // '소유자만'(G)은 상세검색이 아니라 검색줄의 '소속자 포함' 스위치가 정한다(state.withSub).
        // 기본은 켜짐 = 소속자 포함 — PC /adm/members 기사명 스위치와 같은 기본값.
        this.adv = { sel: new Set(), x3: '', x5: '' };
        this.state.withSub = true;
        this.$adv      = $('#maMemAdvanced');
        this.$advToggle = $('#maMemAdvToggle');
        this.$advCount = $('#maMemAdvCount');

        this.bind();
        this.bindAdvanced();

        // 불러톡에서 돌아온 자리면 보던 목록(조건·불러온 건수·스크롤)을 그대로 되세운다
        const back = takeListReturn('members');
        this.bindSosok(back ? back.so : '');
        if (back) this.restoreList(back);
        else this.reload();
        this.openFromUrl();
    }

    /**
     * 불러톡에 다녀온 목록을 되세운다 — openChat() 이 적어 둔 것(listReturn.js).
     * 불러왔던 건수만큼을 **한 번에** 받아 그린 뒤 스크롤을 맞춘다. 쪽 단위로 나눠 받으면
     * 문서가 아직 짧아 보던 자리까지 내려가지 못한다.
     */
    restoreList(back) {
        this.state.st = back.st || 'S0';
        this.$chips.find('.ma-chip').each((_, c) => {
            $(c).toggleClass('is-active', $(c).data('st') + '' === this.state.st);
        });
        $('#maMemKw').val(back.kw || '');
        this.state.withSub = back.withSub !== false;
        $('#maMemSubToggle').toggleClass('is-on', this.state.withSub).attr('aria-checked', this.state.withSub);

        this.adv = { sel: new Set(back.sel || []), x3: back.x3 || '', x5: back.x5 || '' };
        this.$adv.find('.ma-adv-opts[data-multi] .ma-adv-opt').each((_, b) => {
            $(b).toggleClass('is-on', this.adv.sel.has($(b).data('opt') + ''));
        });
        this.$adv.find('.ma-adv-opts[data-single]').each((_, g) => {
            const key = $(g).data('single');
            $(g).find('.ma-adv-opt').each((_, b) => {
                $(b).toggleClass('is-on', ($(b).data(key) != null ? $(b).data(key) : '') + '' === this.adv[key]);
            });
        });
        this.updateAdvCount();

        this.state.start = 0;
        this.rows = {};
        this.$list.empty();
        this.$empty.prop('hidden', true);
        this.fetchList({ length: Math.max(+back.count || 0, LENGTH), after: () => restoreScroll(back.y) });
    }

    /**
     * ?open={회원번호} 로 진입하면 그 회원 상세를 바로 편다 — 불러톡에서 되돌아온 자리.
     * 목록 응답을 기다리지 않는다. openMember() 가 상세를 자체 조회하므로 목록에 그 회원이
     * 없어도(검색조건 밖) 시트는 열린다.
     */
    openFromUrl() {
        const n = parseInt(new URLSearchParams(location.search).get('open'), 10) || 0;
        if (n) this.openMember(n);
    }

    bind() {
        this.$chips.on('click', '.ma-chip', (e) => {
            const $c = $(e.currentTarget);
            this.$chips.find('.ma-chip').removeClass('is-active');
            $c.addClass('is-active');
            this.state.st = $c.data('st') + '';
            this.reload();
        });

        $('#maMemSearchBtn').on('click', () => this.reload());

        // 소속자 포함 스위치 — 폰이라 마우스오버 툴팁이 없다. 바뀐 뜻을 토스트로 알리고 바로 다시 찾는다
        $('#maMemSubToggle').on('click', (e) => {
            const on = !this.state.withSub;
            this.state.withSub = on;
            $(e.currentTarget).toggleClass('is-on', on).attr('aria-checked', on);
            this.toast(on ? '소속자 포함 — 소유자의 소속자까지 찾습니다' : '소유자만 찾습니다');
            this.reload();
        });
        $('#maMemKw').on('keydown', (e) => { if (e.key === 'Enter') this.reload(); });
        this.bindKwAc();
        this.$moreBtn.on('click', () => this.fetchList());

        // 무한 스크롤 — '더보기' 자리가 화면에 들어오면 자동으로 다음 쪽을 붙인다.
        // attach 가 null 이면(IntersectionObserver 미지원) 위 버튼이 그대로 폴백으로 남는다.
        this._infinite = $j.Ui.InfiniteScroll.attach(this.$more[0], { onLoad: () => this.fetchList() });
        if (this._infinite) this.$more.addClass('is-auto');

        // 카드(업종패널·본문) 탭 → 상세 시트 / 톡 버튼은 채팅방으로 (버블 차단)
        this.$list.on('click', '.ma-mem-talk', (e) => {
            e.stopPropagation();
            const $b = $(e.currentTarget);
            this.openChat($b.data('num'));
        });
        this.$list.on('click', '[data-open]', (e) => {
            this.openMember($(e.currentTarget).data('open'));
        });
    }

    /**
     * 불러톡 상담 — 방번호 = 회원번호. 공유 대화창(/ma/chat/room)을 바로 연다.
     *
     * 돌아올 곳(back)을 함께 넘긴다. 대화창의 ← 는 기본값이 채팅 목록이라, 기사목록에서 들어간
     * CS 가 나오면 엉뚱한 화면에 떨어진다. 되돌릴 곳은 **누른 자리 그대로 기사목록**이다 —
     * 톡은 카드에만 달려 있어 상세를 연 적이 없는데, 나올 때 상세시트가 펴지면 안 연 화면이
     * 뜬다. location.replace 라 히스토리로는 못 돌아온다 — 주소로 명시해야 하는 이유다.
     */
    openChat(num) {
        const n = parseInt(num, 10) || 0;
        if (!n) return;
        saveListReturn('members', {
            st: this.state.st,
            kw: ($('#maMemKw').val() || '').trim(),
            withSub: this.state.withSub,
            sel: [...this.adv.sel], x3: this.adv.x3, x5: this.adv.x5,
            so: this.sosokCode(),
            count: this.state.start,   // 불러온 건수 — 돌아와서 이만큼을 한 번에 다시 받는다
        });
        location.replace(`/ma/chat/room?rno=${n}&back=${encodeURIComponent('/ma/members')}`);
    }

    // ───────── 상세검색 ─────────

    bindAdvanced() {
        this.$advToggle.on('click', () => {
            const open = this.$adv.prop('hidden');
            this.$adv.prop('hidden', !open);
            this.$advToggle.attr('aria-expanded', open ? 'true' : 'false').toggleClass('is-open', open);
        });

        // 다중선택(업종·조건) — 고른 글자만 opt 로 나간다
        this.$adv.on('click', '.ma-adv-opts[data-multi] .ma-adv-opt', (e) => {
            const $b = $(e.currentTarget);
            const letter = $b.data('opt') + '';
            if ($b.hasClass('is-on')) { $b.removeClass('is-on'); this.adv.sel.delete(letter); }
            else { $b.addClass('is-on'); this.adv.sel.add(letter); }
            this.updateAdvCount();
        });

        // 단일선택(회원유형 x3 / 미접속 x5)
        this.$adv.on('click', '.ma-adv-opts[data-single] .ma-adv-opt', (e) => {
            const $b = $(e.currentTarget);
            const $grp = $b.closest('.ma-adv-opts');
            $grp.find('.ma-adv-opt').removeClass('is-on');
            $b.addClass('is-on');
            const key = $grp.data('single');
            this.adv[key] = ($b.data(key) != null ? $b.data(key) : '') + '';
            this.updateAdvCount();
        });

        $('#maMemAdvApply').on('click', () => {
            this.$adv.prop('hidden', true);
            this.$advToggle.removeClass('is-open').attr('aria-expanded', 'false');
            this.reload();
        });
        $('#maMemAdvReset').on('click', () => this.resetAdvanced());
    }

    /** 소속 3단 셀렉트 — $j.Common.Form 의 /ajax/sosok 헬퍼 (접수 상세검색과 같은 방식) */
    bindSosok(so = '') {
        // 복귀(so 있음) — 옵션은 ajax 로 늦게 오는데 목록 조회는 지금 나간다. 그 사이 sosokCode() 가
        // 읽을 값을 임시 옵션으로 세워 둔다. 응답이 오면 헬퍼가 옵션을 갈아 끼우며 같은 값을 고른다.
        if (so) {
            [['maMemX', 3], ['maMemY', 6], ['maMemZ', 9]].forEach(([id, len]) => {
                if (so.length >= len) $(`#${id}`).append($('<option>').val(so.substring(0, len))).val(so.substring(0, len));
            });
        }
        $j.Common.Form.updateBonsaSelectBox(so, 'maMemX', 'maMemY', 'maMemZ');
        $j.Common.Form.registerBonsaHandler('maMemX', 'maMemY', 'maMemZ');
        $j.Common.Form.registerGisaHandler('maMemY', 'maMemZ');
        this.$adv.on('change', '#maMemX, #maMemY, #maMemZ', () => this.updateAdvCount());
    }

    /** 소속 — 좁은 쪽(센터>지사>본사)이 이긴다. 서버는 x1 하나로 prefix 검색한다 */
    sosokCode() {
        return ($('#maMemZ').val() || '') || ($('#maMemY').val() || '') || ($('#maMemX').val() || '');
    }

    resetAdvanced() {
        this.adv = { sel: new Set(), x3: '', x5: '' };   // 소유자만/소속자 포함은 검색줄 스위치 몫이라 건드리지 않는다
        this.$adv.find('.ma-adv-opts[data-multi] .ma-adv-opt').removeClass('is-on');
        this.$adv.find('.ma-adv-opts[data-single]').each((_, g) => {
            const $g = $(g);
            $g.find('.ma-adv-opt').removeClass('is-on').first().addClass('is-on');
        });
        $('#maMemX, #maMemY, #maMemZ').val('');
        $j.Common.Form.updateBonsaSelectBox('', 'maMemX', 'maMemY', 'maMemZ');
        this.updateAdvCount();
        this.reload();
    }

    /** 켜진 조건 수 → 토글 배지 */
    updateAdvCount() {
        let n = this.adv.sel.size;
        if (this.adv.x3) n++;
        if (this.adv.x5) n++;
        if (this.sosokCode()) n++;
        this.$advCount.text(n || '').prop('hidden', n === 0);
    }

    /**
     * 검색칸 자동완성 — 이름을 치면 후보를 띄우고, 고르면 `이재일#2831` 로 채운 뒤 바로 검색한다.
     *
     * 검색칸 규약대로 **보이는 텍스트가 곧 기준**이다. 회원번호를 hidden 에 숨기면 표시값과
     * 어긋나도 CS 가 알 수 없다 — `#뒤 숫자`를 params() 가 그대로 읽는다.
     *
     * ⚠ 후보 집합은 목록 검색보다 **좁다.** 정본(`MemberQueryService::findOwnerAutoComplete`)이
     * `mem_status=0 and mem_permit='소유자'` 로 잘라서 소속자·탈퇴자는 후보에 안 뜬다.
     * 후보가 없어도 그냥 검색하면 목록은 그대로 찾아준다 — 자동완성은 지름길이지 관문이 아니다.
     * (넓히려면 데스크탑과 공유하는 그 정본을 손대야 해서 여기서 임의로 바꾸지 않았다)
     *
     * 목록은 띄우지 않고 인풋 아래로 밀어내며 그린다 — 접수 홀드모달(Order.bindMemberAc)과 같은 규격.
     */
    bindKwAc() {
        const $in = document.getElementById('maMemKw');
        const $box = document.getElementById('maMemKwAc');
        if (!$in || !$box) return;

        const hide = () => { $box.hidden = true; $box.innerHTML = ''; };

        const draw = (list) => {
            if (!list.length) return hide();
            $box.innerHTML = list.slice(0, 20).map((r) => `
                <button type="button" class="ma-ac-item" data-num="${r.num}" data-name="${this.esc(r.mem_name || '')}">
                    <span class="n">${this.esc(r.mem_name || '-')}<em>#${r.num}</em></span>
                    <span class="p">${this.esc(r.mem_phone || '')}</span>
                </button>`).join('');
            $box.hidden = false;
            $box.querySelectorAll('.ma-ac-item').forEach((b) => {
                b.onclick = () => {
                    $in.value = `${b.dataset.name}#${b.dataset.num}`;
                    hide();
                    $in.blur();          // 폰 키보드를 내려 결과가 바로 보이게
                    this.reload();       // 고르는 것이 곧 검색이다 — 한 번 더 누르게 하지 않는다
                };
            });
        };

        const run = Util.debounce(() => {
            const kwd = ($in.value || '').trim();
            // 이미 고른 상태('이름#번호')거나 한 글자면 굳이 묻지 않는다
            if (kwd.length < 2 || /#\d+$/.test(kwd)) return hide();
            $.getJSON('/ma/ajax/members/autocomplete', { kwd })
                .done((list) => draw(Array.isArray(list) ? list : []))
                .fail(hide);
        }, 250);

        $in.addEventListener('input', run);
        // 검색·엔터로 직접 찾으면 후보는 역할이 끝났다
        $('#maMemSearchBtn').on('click', hide);
        $in.addEventListener('keydown', (e) => { if (e.key === 'Enter') hide(); });
    }

    params() {
        const kw = ($('#maMemKw').val() || '').trim();
        const p = { x2: this.state.st, length: LENGTH, start: this.state.start, draw: 1 };
        // 검색칸 하나로 세 가지를 받는다. **보이는 텍스트가 곧 검색기준이다** — hidden 이나
        // data-value 에 회원번호를 숨겨두지 않는다(.ai/coding-rules · CLAUDE.md 검색칸 규약).
        //   '이재일#2831' → 자동완성에서 고른 한 명 (회원번호 2831)
        //   '2831'        → 회원번호
        //   '이재일'       → 통합검색
        // 통합검색 색인(g5_member_search)에는 아이디·이름·전화·상호·대표자·차량번호만 들어 있고
        // **회원번호는 없다**. 그래서 '2831' 을 xb 로 보내면 그 회원이 안 잡힌다.
        // x6 로 보내면 서버가 서비스상태 필터도 건너뛰어(PC 와 같은 규칙) 탈퇴자까지 찾아준다.
        //
        // '소속자 포함' 스위치(state.withSub) —
        //   꺼짐 : opt 에 G(소유자만)를 더한다
        //   켜짐 : 고른 한 명은 x7(소유자코드)로 — 그 소유자와 소속자 전원.
        //          통합검색은 xbs=1 — 걸린 소유자의 소속자까지 (서버 MemberController@list)
        //   회원번호 직접입력은 스위치와 무관하게 그 한 명이다 (소속자 번호일 수도 있다)
        if (kw) {
            const picked = kw.match(/#(\d+)$/);
            if (picked) p[this.state.withSub ? 'x7' : 'x6'] = picked[1];   // 자동완성에서 고른 소유자
            else if (/^\d{1,7}$/.test(kw)) p.x6 = kw;                      // 회원번호
            else {
                p.xb = kw;                                                 // 통합검색
                if (this.state.withSub) p.xbs = '1';
            }
        }
        if (this.adv) {
            const opt = [...this.adv.sel].join('') + (this.state.withSub ? '' : 'G');
            if (opt) p.opt = opt;                              // 업종(A~F) + 조건(G,H,I,J,K,M,L)
            if (this.adv.x3) p.x3 = this.adv.x3;               // 회원유형
            if (this.adv.x5) p.x5 = this.adv.x5;               // 미접속
            const so = this.sosokCode();
            if (so) p.x1 = so;                                 // 소속(본사/지사/센터)
        }
        return p;
    }

    reload() {
        this.state.start = 0;
        this.rows = {};
        this.$list.empty();
        this.$empty.prop('hidden', true);
        this.fetchList();
    }

    /**
     * @param {object}   [opts]
     * @param {number}   [opts.length] 이번 한 번만 받을 건수 (복귀 시 보던 만큼 한 번에)
     * @param {Function} [opts.after]  그린 뒤 1회
     */
    fetchList(opts = {}) {
        if (this.state.loading) return;
        this.state.loading = true;
        this.$loading.prop('hidden', false);
        this.$more.prop('hidden', true);

        const params = this.params();
        if (opts.length) params.length = opts.length;

        $.getJSON('/ma/ajax/members/list', params)
            .done((res) => {
                this.state.total = res.recordsTotal || 0;
                this.$sum.html(`<b class="n">${this.num(this.state.total)}</b>명`); // 접수 헤더와 같은 규약

                const rows = res.data || [];
                rows.forEach((r) => {
                    this.rows[r.num] = r;
                    this.$list.append(this.cardHtml(r));
                });
                this.state.start += rows.length;

                this.$empty.prop('hidden', this.state.total !== 0);
                // rows 가 0건이면 total 이 뭐라 하든 멈춘다 — 무한 스크롤은 '더보기'와 달리 사람이
                // 멈춰주지 않아서, total 과 실제 행수가 어긋나면 같은 요청을 끝없이 반복한다.
                this.$more.prop('hidden', !(rows.length > 0 && this.state.start < this.state.total));
                if (typeof opts.after === 'function') opts.after();
            })
            .fail((xhr) => this.toast(xhr.status === 401 ? '세션이 만료되었습니다' : '목록을 불러오지 못했습니다'))
            .always(() => {
                this.state.loading = false;
                this.$loading.prop('hidden', true);
            });
    }

    /**
     * 기사 카드 — 폰 한 화면 기준으로 짠다. 강조축은 PC(/adm/members)와 같지만
     * (업종·본사·지사·소유자/소속자·기종) **PC 처럼 좌우로 칸을 나누지 않는다.**
     * 폰 폭(360~390px)에서 왼쪽 색면 컬럼 + 오른쪽 본문으로 나누면 본문이 200px 밖에 안 남아
     * 지사명·기종이 잘린다. 그래서 줄마다 축 하나씩, 폭은 전부 쓴다.
     *   1줄 이름 · 회원번호 · 전화        ┐
     *   2줄 업종칩 · 소유자/소속자 · 소속(본사·지사) · 상태   ├ 본문
     *   3줄 보유캐시 · 지역(차고지) — 수발주자만  │
     *   4줄 기종 (PC 와 같은 색: 기종분류 blue · 미터 red · 옵션 갈색 · 차량번호 green) ┘
     *   톡(불러톡) 버튼 — 1줄 이름 오른쪽 끝
     * 누가 누구인지(이름·번호·전화)를 맨 위에 두고, 분류축(업종·권한·소속)을 그 아래 한 줄로 모았다.
     */
    cardHtml(r) {
        const st = +r.mem_status;
        const isDriver = r.mem_driver_type === '수발주자';

        // 업종 — PC 는 업종셀 배경색. 폰에서는 같은 색의 칩으로 (색 언어는 유지, 폭은 안 뺏는다)
        const typeChip = isDriver
            ? `<span class="ma-mem-type" style="background:${CAR_TYPE_BG[r.mem_car_type] || '#FFFFFF'}">${this.esc(r.mem_car_type || '-')}</span>`
            : `<span class="ma-mem-type is-baljoo">${this.esc(r.mem_driver_type || '-')}</span>`;
        const permit = r.mem_permit === '소유자'
            ? '<span class="ma-mem-permit is-owner">소유자</span>'
            : `<span class="ma-mem-permit">소속자${+r.is_owner_auth === 1 ? '<b>[소]</b>' : ''}</span>`;
        // 서비스상태 — 정상은 안 붙인다(기본 필터가 정상이라 전 행에 같은 값이 반복될 뿐).
        // 대기·탈퇴신청·탈퇴만 붙이고, PC(gray-row) 처럼 카드 전체를 흐리게 한다.
        const status = st === 0 ? ''
            : `<span class="ma-mc-status s-m${st}">${this.esc(MEM_STATUS[st] || st)}</span>`;
        const offCls = st === 0 ? '' : (st === 1 ? ' is-dim' : ' is-off');

        // 소속(본사 pill + 지사·센터) — 권한 뱃지 바로 옆. 지사명이 길면 이 칸만 말줄임된다
        const bonsa = r._bonsaName
            ? `<span class="ma-mem-bonsa ${BONSA_CLS[r._bonsaCls] || 'ma-bx-all'}">${this.esc(r._bonsaName)}</span>` : '';
        const gisa = r._gisaName ? `<span class="ma-mem-gisa">${this.safeHtml(r._gisaName)}</span>` : '';

        // 3줄 — 보유캐시 · 지역(차고지). **수발주자 카드에만** 붙인다.
        // 발주자는 장비가 없어 차고지가 매칭에 안 쓰이고, 캐시도 수주 수수료·회비가 있는 쪽 얘기라
        // 발주자 카드에 두면 뜻 없는 줄이 하나 늘 뿐이다.
        //   _addr = 차고지 주소를 시/군/동까지 줄인 것 (Address::toSimple2, PC 지역칸과 같은 값)
        //   _cash = 소유자 전유 (소속자는 캐시가 없어 빈 문자열로 온다)
        const area = (isDriver && r._addr)
            ? `<span class="ma-mem-area"><i class="fas fa-location-dot"></i><b>${this.esc(r._addr)}</b></span>` : '';
        const cashV = (isDriver && r.mem_permit === '소유자')
            ? `<span class="ma-mem-cash"><i>캐시</i>${this.esc(this.num(r._cash))}원</span>` : '';
        const meta = (area || cashV) ? `<div class="ma-mem-meta">${cashV}${area}</div>` : '';
        // 불러톡 — 이름줄 오른쪽 끝의 작은 버튼. 예전엔 카드 왼쪽 세로 전체를 쓰는 레일이었는데
        // 본문 폭을 66px 잡아먹는 데 비해 쓰임이 적었다. 톡이 되는 회원(mobie_ver>=2, PC '톡'
        // 컬럼과 같은 조건)에게만 낸다 — 안 되는 회원은 자리도 비운다(이름줄이라 정렬이 안 틀어진다).
        const talk = +r.mobie_ver >= 2
            ? `<button type="button" class="ma-mem-talkbtn ma-mem-talk" data-num="${r.num}" aria-label="불러톡"><i class="fas fa-comment-dots"></i></button>`
            : '';

        return `<div class="ma-row ma-row--mem${offCls}" data-num="${r.num}" data-open="${r.num}">
            <div class="ma-mem-body">
                <div class="ma-mem-nameline">
                    ${this.penaltyCard(r.ycard)}
                    <span class="ma-mem-name">${this.esc(r.mem_name || '')}</span><em>#${r.num}</em>
                    <span class="ma-mem-phone">${this.esc(r.mem_phone || '')}</span>
                    ${talk}
                </div>
                <div class="ma-mem-hd">
                    ${typeChip}${permit}${bonsa}${gisa}${status}
                </div>
                ${meta}
                ${r._car ? `<div class="ma-mem-car">${this.safeHtml(r._car)}</div>` : ''}
            </div>
        </div>`;
    }

    /** 제재 딱지 — PC 이름칸의 경고/제재 카드와 같은 뜻 (g5_member_owners.penalty) */
    penaltyCard(v) {
        const n = parseInt(v, 10) || 0;
        if (n <= 0) return '';
        const [title, cls] = n >= 3 ? ['환원페널티', 'ban'] : n >= 2 ? ['제재회원', 'ban'] : ['경고회원', 'warn'];
        return `<span class="ma-mem-pcard is-${cls}" title="${title}"></span>`;
    }

    /**
     * 서버가 만든 표시용 HTML(_car·_gisaName — 기종은 색까지 PC 와 같다)을 그대로 쓰되
     * 태그는 화이트리스트(b/font/span)만, 속성은 style·class·color 만 남긴다.
     * 값 안에 사용자가 넣은 문자열(차량번호·차량옵션 등)이 이스케이프 없이 섞여 온다.
     * 정규식으로 거르면 `<span/onclick=…>` 같은 변형이 빠져나가서, 실행되지 않는 <template> 에 파싱해 DOM 으로 거른다.
     */
    safeHtml(html) {
        const tpl = document.createElement('template');
        tpl.innerHTML = String(html == null ? '' : html);
        const TAGS = ['B', 'FONT', 'SPAN'], ATTRS = ['style', 'class', 'color'];
        tpl.content.querySelectorAll('*').forEach((el) => {
            if (!TAGS.includes(el.tagName)) { el.replaceWith(document.createTextNode(el.textContent)); return; }
            [...el.attributes].forEach((a) => { if (!ATTRS.includes(a.name)) el.removeAttribute(a.name); });
        });
        return tpl.innerHTML;
    }

    // ───────── 상세 시트 ─────────

    /**
     * @param {number}   num     회원번호
     * @param {Function} [onClose] 시트가 닫힐 때 한 번 부른다 — 오더 상세에서 이름을 눌러 들어온
     *   경우 ✕ 로 그 오더 시트로 되돌아가는 통로다. BS 는 한 장이라 새로 열면 앞 시트가 지워진다.
     */
    openMember(num, onClose) {
        const self = this;
        this.onClose = onClose || null;
        this.tab = 'info';
        this.editing = null;    // 편집 중인 블록 키 (BLOCK_EDIT) — null 이면 읽기
        this.staged  = {};      // [저장] 을 눌러야 나가는 값 (계좌 입력·복사)
        this.carList = null;    // 이 회원의 장비 목록 — 앞서 본 회원 것이 남으면 남의 장비를 고친다
        this.cache = {};        // 탭별 응답 캐시 (상담·기록은 열 때 1회만 요청)
        $j.Ui.BS.open({
            fixedHeight: true,  // 탭이 있어 높이를 고정한다 (접수 상세와 동일)
            backClose: true,    // 안드로이드 뒤로가기 = 시트 닫기 (목록 스크롤·검색조건 보존)
            target: {
                init() {
                    self.curBs = this.parent;
                    self.curNum = num;
                    const el = document.getElementById(this.id);
                    self.sheetEl = el;
                    el.innerHTML = '<div class="ma-order-loading">불러오는 중…</div>';
                    this.parent.setTitle(`기사 #${num}`);
                    $.getJSON(`/ma/ajax/member/${num}`)
                        .done((res) => {
                            if (!res || res.result === false) { el.innerHTML = '<div class="ma-sh-note">회원을 찾을 수 없습니다</div>'; return; }
                            self.curSum = res.summary;
                            self.curDetail = res.detail || {};
                            self.renderDetail(el, res.summary);
                        })
                        .fail(() => { el.innerHTML = '<div class="ma-sh-note">불러오지 못했습니다</div>'; });
                },
                // 닫힘(✕·뒤로가기·다른 시트로 전환) — 되돌아갈 곳이 있으면 한 번만 부른다
                destroy() {
                    const back = self.onClose;
                    self.onClose = null;
                    if (typeof back === 'function') back();
                },
            },
        });
    }

    renderDetail(el, s) {
        const chips = [
            ['상태', s.statusText || ''],
            ['유형', [s.driverType, s.permit].filter(Boolean).join('·')],
            ['소속', s.sosokName || '-'],
            ['가입', (s.createdAt || '').slice(0, 10)],
        ];
        // 요약 칩(상태·유형·소속·가입) 아래 서류보기 — 서류가 있는 회원에게만.
        // 서류 화면은 회원 앱에만 있어서 대리진입으로 연다(데스크탑 regdocuments 와 같은 경로).
        // 대리진입이므로 그 화면에는 '관리자 복귀' 알약이 저절로 뜬다.
        // 서류보기는 요약 칩과 **같은 카드 안**에 둔다. 밖에 두면 아래 블록들의 [수정] 알약과
        // 생김새가 겹쳐 무슨 버튼인지 구분이 안 됐다. 색도 반전(파란 판·흰 글씨)해서
        // '고치는 버튼'(흰 판·파란 글씨)과 성격이 다르다는 걸 보이게 한다.
        const docBtn = (this.curDetail || {}).docHas
            ? '<button type="button" class="ma-doc-btn" data-act="docs">'
              + '<i class="fas fa-folder-open"></i>서류보기</button>'
            : '';
        this.chipsHtml = `<div class="ma-md-grid cols2">${chips.map((c) => this.kv(c[0], c[1])).join('')}${docBtn}</div>`;

        const tel = s.phone
            ? `<a class="ma-tel" href="tel:${this.esc(s.phone)}"><i class="fas fa-phone"></i> ${this.esc(s.phone)}</a>`
            : '';

        el.innerHTML = `
            <div class="ma-sheet">
                <div class="ma-md-head">
                    <div class="ma-md-headL">
                        <div class="ma-md-name">${this.esc(s.name || '')} <small>${this.esc(s.memId || '')}</small></div>
                        <div class="ma-md-phone">${tel}</div>
                    </div>
                    ${this.proxyBtns(+s.status)}
                </div>
                <div class="ma-tabs" role="tablist">
                    ${TABS.map((t) => `<button type="button" class="ma-tab${t.key === this.tab ? ' is-on' : ''}" data-tab="${t.key}" role="tab">${t.label}</button>`).join('')}
                </div>
                <div class="ma-tabbody" id="maMdBody"></div>
            </div>`;

        el.querySelectorAll('.ma-tab').forEach((b) => {
            b.onclick = () => this.switchTab(b.dataset.tab);
        });
        el.querySelectorAll('.ma-md-head [data-act]').forEach((b) => {
            b.onclick = () => this.onAct(el, b.dataset.act);
        });
        this.renderTab();
    }

    switchTab(key) {
        if (!key || key === this.tab) return;
        // 편집 중 탭을 옮기면 입력이 사라진다 — 고친 칸이 있으면 막고, 없으면 조용히 편집을 끝낸다
        if (this.editing) {
            if (this.dirtyEdits()) return this.toast('저장 또는 취소 후 이동해주세요');
            this.editing = null;
        }
        this.tab = key;
        const el = this.sheetEl;
        if (!el) return;
        el.querySelectorAll('.ma-tab').forEach((b) => b.classList.toggle('is-on', b.dataset.tab === key));
        this.renderTab();
    }

    /** 현재 탭 본문 렌더 — 상담·기록은 처음 열 때만 요청하고 캐시한다 */
    renderTab() {
        const el = this.sheetEl;
        const box = el && el.querySelector('#maMdBody');
        if (!box) return;

        // 정보 탭은 매번 다시 만든다 — 어느 블록이 편집 중이냐에 따라 모습이 달라진다
        if (this.tab === 'info') box.innerHTML = this.infoTabHtml();
        // 저장바가 화면 맨 아래에 떠 있는 동안에는 본문 끝이 바에 가리지 않게 자리를 비운다
        box.classList.toggle('has-editbar', this.tab === 'info' && !!this.editing);
        if (this.editing === 'car')   this.mountCarSelect();   // 셀렉트가 DOM 에 선 뒤에 붙인다
        if (this.editing === 'sosok') this.mountSosok();
        this.markEditing(box);
        if (this.tab === 'act')  box.innerHTML = this.tabAct();
        if (this.tab === 'counsel') this.renderCounsel(box);
        if (this.tab === 'log')     this.renderLoginLogs(box);

        box.querySelectorAll('[data-act]').forEach((b) => {
            b.onclick = () => this.onAct(el, b.dataset.act);
        });
        // 돈·자원 블록의 버튼줄 (내역·설정·재계산·지급)
        box.querySelectorAll('[data-mact]').forEach((b) => {
            b.onclick = () => this.onMact(b.dataset.mact);
        });

        // 편집모드 — 연필(on) / 하단 저장바(off·save)
        box.querySelectorAll('[data-editmode]').forEach((b) => {
            const m = b.dataset.editmode;
            b.onclick = () => (m === 'save' ? this.saveEdits() : this.toggleEdit(m === 'on' ? b.dataset.block : null));
        });
    }

    /** 관리 탭 — 상태변경·푸시테스트 (대리진입은 시트 머리로, 정보수정은 정보 탭으로 옮겼다) */
    tabAct() {
        const st = +(this.curSum || {}).status;
        const btns = [];
        if (st === 0) btns.push(['standby', '대기전환', '']);
        if (st !== 0) btns.push(['rollback', st === 2 ? '휴면해제' : '정상복구', 'primary']);   // 휴면(2)도 같은 rollback 으로 푼다
        if (st !== 99) btns.push(['leave', '탈퇴', 'danger']);

        return `<div class="ma-sh-sec"><h4>상태 변경</h4><div class="ma-act-btns">
                ${btns.map((b) => `<button type="button" class="ma-ab-btn ${b[2]}" data-act="${b[0]}">${b[1]}</button>`).join('')}
            </div></div>
            <div class="ma-sh-sec"><h4>테스트 발송</h4>
                <p class="ma-sh-desc">수주는 이 기사에게만 가는 어플오더를 실제로 만듭니다 (내일 09:00 · 차고지 기준).</p>
                <div class="ma-act-btns row3">
                    <button type="button" class="ma-ab-btn" data-act="push-register">접수</button>
                    <button type="button" class="ma-ab-btn" data-act="push-match">매칭</button>
                    <button type="button" class="ma-ab-btn" data-act="order-test">수주</button>
                </div></div>`;
    }

    /**
     * 정보 탭 본문 — 요약 칩 + 블록들. 편집은 블록마다 제 [수정] 으로 들어간다(BLOCK_EDIT).
     * 소속자에게는 소유자 귀속 묶음의 [수정] 이 애초에 서지 않아 안내 한 줄을 대신 둔다.
     */
    infoTabHtml() {
        const isOwner = ((this.curSum || {}).permit || '') === '소유자';
        const note = isOwner ? ''
            : '<p class="ma-sh-desc">소속자라 연락·사업자 정보는 소유자 계정에서 고칩니다.</p>';
        return (this.chipsHtml || '') + note + this.infoSections(this.curSum || {}, this.curDetail || {});
    }

    /**
     * 편집 대상 묶음 — 소속자에게는 소유자 귀속 묶음을 내지 않는다(EDIT_GROUPS 주석).
     * 묶음 안 예외칸(조건)까지 걸러 낸 뒤, 남는 칸이 없는 묶음은 통째로 뺀다.
     */
    editGroups() {
        const isOwner = ((this.curSum || {}).permit || '') === '소유자';
        const isSamsung = Util.isSosokSamsung(String(((this.curDetail || {}).sosok || {}).code || ''));
        const isCar = ((this.curSum || {}).driverType || '') === '수발주자';
        const ok = (cond) => {
            if (cond === 'owner')      return isOwner;
            if (cond === 'sosok')      return !isOwner;
            if (cond === 'samsung')    return isSamsung;
            if (cond === 'car')        return isCar;
            if (cond === 'permitfree') return !isCar;
            return true;
        };
        return EDIT_GROUPS
            .filter((g) => !g.owner || isOwner)
            .map((g) => Object.assign({}, g, { f: g.f.filter((f) => ok(f[4])) }))
            .filter((g) => g.f.length);
    }

    /** 편집모드에서 실제로 고친 칸이 있나 (탭 이동 경고 판정) */
    dirtyEdits() {
        if (Object.keys(this.staged || {}).length) return true;
        const box = this.sheetEl && this.sheetEl.querySelector('#maMdBody');
        if (!box) return false;
        return this.editGroups().some((g) => g.f.some(([, name, , path]) => {
            const el = box.querySelector(`#maEd_${name}`);
            return el && (el.value || '').trim() !== this.editVal(path).trim();
        }));
    }

    /**
     * 블록 편집 토글 — key 를 주면 그 블록만 입력칸으로, null 이면 읽기로 되돌린다.
     * 취소는 입력만 버린다 (저장된 값은 그대로라 다시 그리면 끝).
     *
     * 다른 블록을 고치는 중이면 먼저 매듭짓게 막는다 — 두 블록을 동시에 열어두면
     * 저장이 어느 쪽 값을 보냈는지 화면만 봐서는 알 수 없다.
     */
    toggleEdit(key) {
        if (key && this.editing && this.editing !== key) {
            return this.toast('저장 또는 취소 후 다른 항목을 수정해주세요');
        }
        this.editing = key || null;
        this.staged = {};       // 취소 = 올려둔 값도 버린다
        if (key === 'car' && this.carList == null) this.loadCars();
        this.renderTab();

        // 켠 블록이 화면 맨 위에서 시작하게 — 시트 중간에서 누르면 입력칸이 접힌 채 열린다
        const box = this.sheetEl && this.sheetEl.querySelector('#maMdBody');
        if (key && box) scrollSecTop(box.querySelector(`[data-sec="${key}"]`), this.sheetEl.querySelector('.ma-tabs'));
    }

    /** 상세응답에서 현재값 꺼내기 — 'sum.name' · 'basic.phone2' 같은 경로 */
    editVal(path) {
        const [head, key] = String(path).split('.');
        const src = head === 'sum' ? (this.curSum || {}) : ((this.curDetail || {})[head] || {});
        const v = src[key];
        return v == null ? '' : String(v);
    }

    /**
     * 필드 하나 — 라벨(왼쪽) │ 입력(오른쪽) 한 줄. 읽기 화면의 `.ma-kv` 와 같은 자리에 같은 폭이라
     * 편집을 켜고 꺼도 값이 제자리에 있다. 줄 사이 구분선은 scss(.ma-ed-row)가 긋는다.
     */
    editField([label, name, kind, path]) {
        // 올려둔 값(지도에서 고른 차고지 등)이 있으면 그쪽이 지금 화면의 값이다
        const v = (this.staged && this.staged[name] != null) ? String(this.staged[name]) : this.editVal(path);
        const lb = `<label class="k" for="maEd_${name}">${this.esc(label)}</label>`;

        // 대표소속 — 본사·지사·센터 3단. 실제로 보내는 값은 가장 하위 선택이라 hidden 에 모은다.
        // 셀렉트 채우기는 common.js 공용 헬퍼가 ajax 로 한다 → 붙이는 것은 renderTab 뒤(mountSosok).
        if (kind === 'sosok3') {
            return `<div class="ma-ed-row">${lb}<span class="v ma-ed-sosok">`
                + '<select class="ma-mdl-in" id="maSsB"><option value="">본사</option></select>'
                + '<select class="ma-mdl-in" id="maSsG"><option value="">지사</option></select>'
                + '<select class="ma-mdl-in" id="maSsC"><option value="">센터</option></select>'
                + `<input type="hidden" id="maEd_${name}" name="${name}" value="${this.esc(v)}">`
                + '</span></div>';
        }

        // 주소칸 — 직접 타이핑하지 않고 **칸을 누르면 지도**가 열린다. 주소만 바꾸고 좌표가
        // 옛것으로 남으면 당일외 매칭이 어긋난다. (별도 [지도] 버튼은 칸이 좁아져서 뺐다)
        if (kind === 'addr') {
            return `<div class="ma-ed-row">${lb}<span class="v">`
                + `<input class="ma-mdl-in is-pick" id="maEd_${name}" name="${name}" type="text" readonly`
                + ` value="${this.esc(v)}" placeholder="눌러서 주소 검색" data-mact="find-addr"></span></div>`;
        }
        if (kind.startsWith('select:')) {
            const list = kind.slice(7).split(',').map((o) => o.split('='));
            const opts = list.map(([ov, ot]) =>
                `<option value="${ov}"${String(ov) === v ? ' selected' : ''}>${this.esc(ot)}</option>`).join('');
            // 지금 값이 고를 수 있는 값에 없으면(예: 위치동의 stale=약관개정 후 재동의필요)
            // 맨 앞에 현재값을 세워 둔다. 없으면 브라우저가 첫 항목을 고른 것처럼 보여 값이 뒤바뀐다.
            const cur = (v !== '' && !list.some(([ov]) => String(ov) === v))
                ? `<option value="${this.esc(v)}" selected>${this.esc(ODD_VALUE[v] || v)}</option>` : '';
            return `<div class="ma-ed-row">${lb}<span class="v">`
                + `<select class="ma-mdl-in" id="maEd_${name}" name="${name}">${cur}${opts}</select></span></div>`;
        }
        const type = (kind === 'date' || kind === 'month') ? kind : (kind === 'num' || kind === 'tel' ? 'tel' : 'text');
        const im   = (kind === 'num' || kind === 'tel') ? ' inputmode="numeric"' : '';
        return `<div class="ma-ed-row">${lb}<span class="v">`
            + `<input class="ma-mdl-in" id="maEd_${name}" name="${name}" type="${type}"${im} value="${this.esc(v)}"></span></div>`;
    }

    /**
     * 편집모드 저장 — 화면에 낸 묶음의 필드를 한 번에 보낸다.
     * 안 보낸 키는 서버가 DB 현재값으로 채우므로(MemberAdminSaveService::fillMissing) 소속자에게
     * 내지 않은 소유자 귀속 필드가 빈값으로 날아가지 않는다.
     * editMode/editnumber 는 데스크탑 폼과 같은 이름이라야 save() 가 '수정'으로 받는다.
     */
    saveEdits() {
        const box = this.sheetEl && this.sheetEl.querySelector('#maMdBody');
        if (!box) return;

        const data = { editMode: 'edit', editnumber: this.curNum };
        this.editGroups().forEach((g) => g.f.forEach(([, name]) => {
            const el = box.querySelector(`#maEd_${name}`);
            if (el) data[name] = (el.value || '').trim();
        }));
        // 버튼으로 올려둔 값(계좌 입력·복사, 지도에서 고른 차고지)도 같은 통로로 나간다
        Object.assign(data, this.staged || {});

        // 소속은 본사만 고르면 3자리라 매칭·정산이 갈 곳을 못 찾는다 (데스크탑 Save 와 같은 기준)
        if (this.editing === 'sosok' && String(data.sosok || '').length < 4) {
            return this.toast('지사까지 선택해주세요');
        }

        // 장비는 다른 테이블이라 요청이 따로 나간다 — 먼저 보내고 성공했을 때만 기사정보를 잇는다
        if (this.editing === 'car') {
            const carReq = this.saveCar();
            if (carReq) {
                return carReq
                    .done(() => this.saveMember(data))
                    .fail((d) => this.toast((d && d.message) || '장비를 저장하지 못했습니다'));
            }
        }
        if (Object.keys(data).length <= 2) { this.editing = null; return this.renderTab(); }  // 바꾼 것이 없다
        this.saveMember(data);
    }

    /** 기사정보 전송 — 보낸 키만 반영된다(MemberAdminSaveService::fillMissing) */
    saveMember(data) {
        $.ajax({ url: '/ma/ajax/member/v2/save', type: 'post', data })
            .done((res) => {
                const r = (res && typeof res === 'object') ? res : {};
                if (r.result === false) return this.toast(r.message || '저장하지 못했습니다');
                this.editing = null;
                this.staged = {};
                this.carList = null;    // 장비가 바뀌었을 수 있다 — 다음에 열 때 다시 받는다
                this.toast('저장했습니다');
                this.reopen();
            })
            .fail(() => this.toast('저장에 실패했습니다'));
    }

    /** 저장 뒤 상세를 다시 받아 시트를 갱신한다 (목록도 값이 바뀌었을 수 있어 함께 새로고침) */
    reopen() {
        const n = this.curNum;
        $.getJSON(`/ma/ajax/member/${n}`).done((res) => {
            if (!res || res.result === false) return;
            this.curSum = res.summary;
            this.curDetail = res.detail || {};
            if (this.sheetEl) this.renderDetail(this.sheetEl, res.summary);
        });
        this.reload();
    }

    /**
     * 회원 앱 대리진입 — 시트 머리 오른쪽, 이름 옆의 알약.
     *
     * '대신 발주해달라'는 전화를 받은 자리에서 바로 누르는 버튼이라 이름·전화 옆이 제자리다
     * (관리 탭에 두면 상태변경·탈퇴 같은 무거운 버튼들 사이에서 한 번 더 찾아 들어가야 했다).
     * 탈퇴완료(99)는 서버(ProxyLoginService::byAdmin)가 아예 막으므로 버튼을 내지 않는다.
     */
    proxyBtns(st) {
        if (st === 99) return '';
        return `<div class="ma-md-proxy">
            <button type="button" class="ma-hd-btn primary" data-act="proxy-bal"><i class="fas fa-pen-to-square"></i>대신발주</button>
            <button type="button" class="ma-hd-btn" data-act="proxy-home" aria-label="회원 앱 홈"><i class="fas fa-mobile-screen"></i></button>
        </div>`;
    }

    /** 상담 탭 — 상담추가 버튼 + 상담 내역(줄마다 수정) */
    renderCounsel(box, force) {
        const head = '<div class="ma-sh-sec"><div class="ma-act-btns">'
            + '<button type="button" class="ma-ab-btn primary wide" data-act="counsel-add">상담추가</button></div></div>';
        const paint = (listHtml) => {
            box.innerHTML = head + listHtml;
            box.querySelectorAll('[data-act]').forEach((b) => { b.onclick = () => this.onAct(this.sheetEl, b.dataset.act); });
            // 상담줄 탭 → 편집창 (줄 전체가 표적 — counselItem 주석)
            box.querySelectorAll('[data-cns-edit]').forEach((b) => {
                b.onclick = () => this.counselEditModal(+b.dataset.cnsEdit);
            });
        };

        if (this.cache.counsel && !force) return paint(this.cache.counsel);
        paint('<div class="ma-order-loading">불러오는 중…</div>');
        $.getJSON(this.url(this.curNum, 'counsels')).done((list) => {
            const arr = Array.isArray(list) ? list : (list && list.list) || [];
            // 편집창이 쓸 원본 — 화면 텍스트를 되읽으면 개행·이스케이프가 뭉개진다
            this.cache.counselRows = arr;
            this.cache.counsel = arr.length
                ? `<div class="ma-cns-list">${arr.map((c) => this.counselItem(c)).join('')}</div>`
                : '<div class="ma-sh-note">상담 내역이 없습니다</div>';
            if (this.tab === 'counsel') paint(this.cache.counsel);
        }).fail(() => paint('<div class="ma-sh-note">불러오지 못했습니다</div>'));
    }

    /**
     * 상담 한 줄 — 유형색은 PC 상담내역과 같다. 제재에서 온 경고줄은 분홍 + 읽기전용 표시.
     *
     * 수정은 **줄 전체를 눌러서** 한다 (기사카드·오더카드와 같은 문법). 연필 같은 표시는 달지
     * 않는다 — 표적이 줄 전체라 아이콘이 가리킬 자리가 따로 없고, 줄마다 붙으면 정작 읽어야 할
     * 유형·날짜 옆에 군더더기만 는다.
     *
     * 고칠 수 있는 줄은 진짜 상담줄뿐이다. 경고줄(src='penalty')은 제재관리가 정본인 **사본**이라
     * 상담번호(num)가 없다 — 고칠 대상 자체가 여기 없으니 표적으로도 만들지 않는다.
     * (경고줄은 분홍 배경·붉은 테두리·집행중/해제 딱지로 이미 다르게 보인다)
     */
    counselItem(c) {
        const type = c.type || '상담';
        const isWarn = c.src === 'penalty' || type === '경고';
        const bg = isWarn ? CNS_WARN_BG : (CNS_BG[type] || '#ffffff');
        const tail = isWarn
            ? `<span class="ma-cns-tag">${c.active ? '집행중' : (c.released_at ? '해제 ' + this.esc(c.released_at) : '해제')}</span>`
            : '';
        const canEdit = !isWarn && +c.num > 0;
        const cls  = 'ma-cns-item' + (isWarn ? ' is-warn' : '') + (canEdit ? ' is-editable' : '');
        const hit  = canEdit ? ` data-cns-edit="${+c.num}"` : '';

        return `<div class="${cls}"${hit} style="background:${bg}">
            <div class="ma-cns-top"><b>${this.esc(type)}</b>${tail}
                <span class="ma-cns-date">${this.esc((c.counsel_date || '').slice(0, 16))}</span></div>
            <div class="ma-cns-body">${this.esc(c.content || '')}</div>
            <div class="ma-cns-by">${this.esc(c.manage_name || '')}</div>
        </div>`;
    }

    /** 기록 탭 — 로그인 기록 */
    renderLoginLogs(box) {
        if (this.cache.log) { box.innerHTML = this.cache.log; return; }
        box.innerHTML = '<div class="ma-order-loading">불러오는 중…</div>';
        $.getJSON(this.url(this.curNum, 'login-logs')).done((res) => {
            const list = (res && res.list) || [];
            this.cache.log = list.length
                // 문구(what·why·where)는 서버가 만든다 — 관리자 PC 모달과 같은 출처(LoginLogService::forMember)
                ? `<div class="ma-logs">${list.map((l) => `<div class="ma-log">
                        <span class="a">${this.esc(l.what || '')}${l.res === 'fail' ? ' — 실패' : ''}${l.why ? ` (${this.esc(l.why)})` : ''}
                            · ${this.esc(l.where || '')} · ${this.esc(l.ip || '')}</span>
                        <span class="t">${this.esc((l.datetime || '').slice(5, 16))}</span></div>`).join('')}</div>`
                : '<div class="ma-sh-note">로그인 기록이 없습니다</div>';
            if (this.tab === 'log') box.innerHTML = this.cache.log;
        }).fail(() => { box.innerHTML = '<div class="ma-sh-note">불러오지 못했습니다</div>'; });
    }

    /**
     * 편집 중 표시 — **고치는 블록만 살리고 나머지는 흐린다.**
     *
     * 블록이 열 개 남짓이라 어디를 고치는 중인지 카드 테두리만으로는 잘 안 읽혔다.
     * 나머지 [수정] 도 함께 죽인다 — 두 블록을 동시에 열면 저장이 어느 쪽 값을 보냈는지
     * 화면만 봐서는 알 수 없다.
     *
     * ⚠ disabled 를 쓰지 않는다. 그러면 click 자체가 안 잡혀 "왜 안 눌리지" 안내를 못 띄운다.
     *   눌리기는 하되 toggleEdit 이 막고 이유를 말한다.
     */
    markEditing(box) {
        const on = this.editing;
        box.querySelectorAll('.ma-sh-sec').forEach((el) => {
            const mine = !!on && el.dataset.sec === on;
            el.classList.toggle('is-editing', mine);
            el.classList.toggle('is-dim', !!on && !mine);
        });
        box.querySelectorAll('.ma-sec-edit[data-block]').forEach((b) => {
            b.classList.toggle('is-off', !!on && b.dataset.block !== on);
        });
    }

    /** 빈 값 자리표시 — 고칠 수 있는 블록은 비어도 줄을 남긴다 (넣을 자리가 보여야 한다) */
    dash(v) { return (v == null || v === '') ? '-' : v; }

    kv(k, v) {
        return `<div class="ma-kv"><span class="k">${this.esc(k)}</span><span class="v">${this.esc(v)}</span></div>`;
    }

    /**
     * 정보 탭 본문 — 데스크탑 기사편집 창(/adm/member/edit)의 4열을 **섹션으로 쌓은 것**.
     * 편집은 데스크탑에서 하고 여기서는 읽기만 한다. 값이 없는 줄·블록은 통째로 뺀다.
     */
    infoSections(s, d) {
        const DRV = { 0: '예비', 1: '공개', 2: '심사중', 9: '퇴사' };
        const b = d.basic || {}, so = d.sosok || {}, co = d.company || {}, dev = d.device || {};
        const dr = so.driver || {};
        const out = [];

        // 유형·상태 — 고치는 칸(EDIT_GROUPS.status)과 **같은 줄만** 모아 둔 블록.
        // 예전엔 이 값들이 머리 칩·기본·소속에 흩어져 있어서, '소속'의 [수정] 을 누르면
        // 소속과 상관없는 기사타입·서비스상태가 열렸다.
        const isOwner = (s.permit || '') === '소유자';
        out.push(this.sec('type', '유형·상태', [
            ['기사타입', s.driverType],
            ['기사권한', s.permit],
            ['서비스상태', s.statusText],
            ['소유자권한', isOwner ? '' : (+so.isOwnerAuth === 1 ? 'ON' : 'OFF')],
            ['위치동의', LOC_LABEL[b.locConsent] || ''],
        ]));

        // 기본 — **편집칸과 같은 줄만** 같은 순서로 둔다. 값이 비어도 '-' 로 남긴다.
        // (아이디·페이앳은 여기서 못 고쳐서 '상태' 블록으로 내렸다 — 각자 전용 창·엔드포인트가 있다)
        const dash = (v) => this.dash(v);
        const real = [b.realName, b.sex, b.sns].filter(Boolean).join(' · ');
        const pwdReset = (this.staged || {}).mem_pwd;
        out.push(this.sec('basic', '기본', [
            ['아이디', dash(b.memId)],
            ['비밀번호', pwdReset ? `핸드폰 뒤 4자리로 초기화 예정 (${pwdReset})` : ''],
            ['실명/성별', dash(real)],
            ['이름', dash(s.name)], ['전화', dash(s.phone)], ['생년월일', dash(b.birthday)],
            ['추가폰', dash(b.phone2)], ['이메일', dash(b.email)],
            ['입사일', isOwner ? '' : dash(dr.enterDate)],        // 소속자만 (g5_member_drivers.co_enter_date)
            ['유료정지예정', b.payStopYm || ''],
            ['사원코드', b.empCode || ''],
            ['마케팅', dash(LOC_LABEL[b.mkConsent] || '')],
            ['통화', +b.isCall === 0 ? '부재중' : '통화완료'],
            ['테스트계정', +b.isTest === 1 ? 'YES' : 'NO'],
        ]));

        // 소속 — 대표소속만 고친다 (BLOCK_EDIT 주석)
        out.push(this.sec('sosok', '소속', [
            ['대표소속', so.name],
            ['추가소속', so.addCnt ? `${so.addCnt}곳` : ''],
            ['소유자', so.ownerText],
            ['소개기사', so.introText],
            ['소속상태', dr.status != null ? (DRV[dr.status] || dr.status) : ''],
            ['퇴사일', dr.outDate],
        ]));

        // 보유장비 · 회비결제방식 · 계좌 · 보유캐시 · 보유자원 (데스크탑 열2·열3)
        out.push(this.assetBlocks(s, d));

        // 사업자 (PC 열3) — 값이 비어도 소유자에게는 블록을 남긴다 (새로 넣을 자리)
        out.push(this.sec('company', '사업자', [
            ['사업자', co.number], ['상호', co.title], ['대표', co.ceo], ['대표생일', co.birth],
            ['전화', co.phone], ['E-mail', co.email], ['팩스', co.fax],
            ['주소', co.address], ['업태', co.uptae], ['업종', co.upjong],
        ]));

        // 상태 — 접속·앱·서류·제재 (PC 열3 '정보'/'제재현황' 탭)
        const info = s.info || {};
        const APP = { latest: '최신', old: '구버전', unsupported: '업데이트 필요' };
        // 기기명·앱버전은 앱이 보내온 값이라 그대로 믿지 않는다 — 배지 마크업만 남기고 값은 이스케이프
        const appVer = dev.ver
            ? `${this.esc(dev.ver)}${APP[info.appState] ? ` <b class="ma-tag ${info.appState}">${APP[info.appState]}</b>` : ''}`
            : '접속기록 없음';
        const login = info.lastLogin
            ? `${this.esc(info.lastLogin)}${info.lastLoginAgo ? ` <b class="ma-tag ${info.staleDays >= 30 ? 'unsupported' : (info.staleDays >= 7 ? 'old' : 'latest')}">${this.esc(info.lastLoginAgo)}</b>` : ''}`
            : '기록없음';
        const e = (v) => (v == null || v === '' ? '' : this.esc(v));
        out.push(this.infoBlock('상태', [
            ['페이앳', e(b.payatId)],
            ['가입', e((s.createdAt || '').slice(0, 16))],
            ['최근접속', login],
            ['앱', appVer],
            ['기기', e([dev.name, dev.os].filter(Boolean).join(' · '))],
            ['푸시토큰', dev.hasToken ? '있음' : '없음'],
            ['서류', `${this.num(d.docCnt)}/${this.num(d.docMax)}`],
            ['탈퇴신청', e(s.leaveAt)],
        ], true));   // 값 안에 배지(HTML)가 있어 이스케이프하지 않는다 — 위에서 값마다 이스케이프했다

        // 제재 — 요약 두 줄 + [제재 관리]. 부과·해제·수정은 그 창에서 한다 (penaltySheet.js).
        // 보는 자리와 손대는 자리를 같게 둔다 — 데스크탑 기사편집의 '제재현황 → [제재 관리]' 와 같은 동선.
        const sc = s.sanctions || {};
        const act = (sc.active || []).map((a) => a.title || a.punish).filter(Boolean).join(' · ');
        const penBtn = '<button type="button" class="ma-sec-edit" data-mact="penalty">'
            + '<i class="fas fa-gavel"></i>제재 관리</button>';
        out.push(this.infoBlock('제재', [
            ['처벌', sc.activeCnt ? `집행중 ${sc.activeCnt}건${act ? ` (${act})` : ''}` : '없음'],
            ['전과', sc.priorCnt ? `${sc.priorCnt}건${sc.lastAt ? ` (최근 ${sc.lastAt})` : ''}` : '없음'],
            ['환원페널티', (sc.whanTotal || sc.whanActive)
                ? `${this.num(sc.whanActive)}/${this.num(sc.whanTotal)}${sc.whanLast ? ` (최근 ${sc.whanLast})` : ''}` : '없음'],
        ], false, penBtn, '', 'penalty'));

        return out.join('');
    }

    /**
     * 돈·장비 블록 — 데스크탑 기사편집창의 **블록 이름을 그대로** 쓴다
     * (보유장비 · 회비결제방식 · 계좌 · 보유캐시 · 보유자원).
     *
     * 예전엔 '장비·매칭'·'회비·계좌'·'보유자원' 셋으로 뭉쳐 있었다. CS 가 PC 화면과 대조하며
     * 쓰는 자리라 이름이 다르면 "빠졌다"고 읽힌다 — 데스크탑과 같은 단위로 가른다.
     *
     * **값이 비어도 블록을 남긴다.** 비면 통째로 사라지던 예전 규칙이면 '이 회원은 계좌가 없다'와
     * '화면이 안 그려졌다'가 구분되지 않는다. 빈 칸은 '-' 로 자리를 지킨다.
     *
     * 편집모드에서도 그대로 다시 쓴다 — 이 다섯은 편집칸이 아니라 읽는 값이라(설정·입출금은
     * 데스크탑 전용 창) 편집을 켰다고 사라지면 안 된다.
     */
    assetBlocks(s, d) {
        const FEE = { 1: '미설정', 3: 'CMS', 4: '선불' };
        const car = d.car || {}, res = d.res || {}, fee = d.fee || {};
        const isCar = (s.driverType || '') === '수발주자';
        const dash = (v) => this.dash(v);
        const out = [];

        // 보유장비 — 수발주자만 낸다 (발주자는 데스크탑도 이 블록이 없다)
        if (isCar || car.type) {
            const spec = [car.meter && car.meter + 'm', car.height && '전고 ' + car.height].filter(Boolean).join(' · ');
            out.push(this.sec('car', '보유장비', [
                ['종류', dash(car.type)], ['제원', dash(spec)], ['옵션', dash(car.option)],
                ['번호판', dash(car.plate)], ['차고지', dash(car.park)],
                ['매칭반경', car.areakm ? `${car.areakm}km` : '-'],
                ['수주승인', +car.isSoojoo === 1 ? 'YES' : 'NO'],
                ['GPS', dash(GPS_LABEL[+car.isGps])],
                ['최근 GPS', dash(car.gpsAt)],
            ]));

            // 회비결제방식 — 월회비는 수발주자에게만 청구한다 (데스크탑 $showCar 와 같은 조건)
            const nofee = +fee.nofee === 1 ? '장기감면' : (+fee.nofee === 2 ? '회비면제' : '');
            out.push(this.sec('fee', '회비결제방식', [
                ['결제방식', dash(fee.text || [FEE[+fee.type] || '', fee.opt].filter(Boolean).join(' '))],   // 받는 길 · 금액 (데스크탑과 같은 한 줄)
                ['감면', dash(nofee)],
            ]));
        }

        // 계좌 — 셋 다 '계좌'지만 돈의 방향이 다르다 (나가는 돈 / 빠지는 회비 / 들어오는 돈).
        // 발주자에게도 낸다 — 돈이 드나드는 창구는 회원 종류를 안 가린다 (데스크탑과 같은 규칙).
        out.push(this.sec('bank', '계좌', [
            ['출금', dash(this.bankShow(1))], ['CMS', dash(this.bankShow(2))], ['가상계좌', dash(fee.virtual)],
        ]));

        // 보유캐시 — KRW 입금으로 채워지는 실화폐. 월회비·수수료로 빠진다 (CS 문의 1순위)
        out.push(this.sec('cash', '보유캐시', [['잔액', `${this.num(res.cash)}원`]]));

        // 보유자원 — 매칭에 쓰는 화폐. 캐시와 성격이 달라 블록을 가른다
        if (isCar) {
            out.push(this.sec('res', '보유자원', [
                ['발주개수', `${this.num(res.orderCnt)}개`],
                ['차수', `${res.chrsoo || 0}차`],
                ['-6차', res.msixCnt ? `${res.msixNday}일차 ${res.msixCnt}개` : '-'],
                ['-6차 pt', res.msixPt ? this.num(res.msixPt) : '-'],
                ['수주포인트', `${this.num(res.point)}P`],
            ]));
        }

        return out.join('');
    }

    /**
     * 편집칸이 있는 블록 — 읽을 때는 제목 옆에 [수정], 누르면 **그 블록만** 입력칸으로 바뀐다.
     * 편집칸이 없는 블록(계좌·캐시·자원 등)은 infoBlock 을 그대로 쓴다.
     *
     * 한 블록을 고치는 동안 다른 블록의 [수정] 은 내리지 않는다 — 대신 누르면 저장/취소를
     * 먼저 하라고 막는다(onEditBlock). 버튼이 사라지면 '왜 없지'로 읽힌다.
     */
    sec(key, title, rows, raw = false) {
        const groups = this.editGroups().filter((g) => (BLOCK_EDIT[key] || []).includes(g.key));
        const st = +(this.curSum || {}).status;
        const acts = st !== 99 ? this.blockActs(key) : '';   // 창을 여는 버튼줄 (돈·자원 블록)

        // 편집 중인 블록 — 입력칸 / 버튼줄 / 딸린 목록을 한 카드에 쌓고 하단 저장바를 띄운다.
        // 셋 다 있는 블록이 있다(보유장비: 매칭칸 + 장비목록).
        if (this.editing === key && (groups.length || acts)) {
            const fields = groups.length
                ? `<div class="ma-md-grid ma-ed-grid">${groups.map((g) => g.f.map((f) => this.editField(f)).join('')).join('')}</div>`
                : `<div class="ma-md-grid">${rows.filter((r) => r[1] != null && r[1] !== '').map((r) => this.kv(r[0], r[1])).join('')}</div>`;
            // 보낼 값이 있는 블록만 [저장] — 나머지는 버튼 하나가 곧 한 건의 처리라 [닫기] 뿐이다
            const bar = (groups.length || STAGE_BLOCKS[key])
                ? '<button type="button" class="ma-ab-btn" data-editmode="off">취소</button>'
                  + '<button type="button" class="ma-ab-btn primary" data-editmode="save">저장</button>'
                : '<button type="button" class="ma-ab-btn wide" data-editmode="off">닫기</button>';
            return `<div class="ma-sh-sec is-editing" data-sec="${key}">`
                + `<h4 class="has-edit">${this.esc(title)}`
                + '<button type="button" class="ma-sec-edit is-on" data-editmode="off"><i class="fas fa-pen"></i>수정중</button></h4>'
                + this.editTop(key)
                + fields
                + (acts ? `<div class="ma-act-btns ma-blk-acts">${acts}</div>` : '')
                + `<div class="ma-edit-bar is-float">${bar}</div></div>`;
        }

        // 탈퇴완료(99)는 편집 대상이 아니다
        const btn = ((groups.length || acts) && st !== 99)
            ? `<button type="button" class="ma-sec-edit" data-editmode="on" data-block="${key}">`
              + '<i class="fas fa-pen"></i>수정</button>'
            : '';
        return this.infoBlock(title, rows, raw, btn, '', key);
    }

    /**
     * 돈·자원 블록의 버튼줄 — 데스크탑 기사편집창에서 그 블록이 달고 있던 버튼을 그대로 옮겼다.
     *
     * 값을 타이핑하는 자리가 아니라 **창을 여는 자리**라 입력묶음(BLOCK_EDIT)과 갈라 둔다.
     * 권한선(재계산 level>=7)·노출조건(수발주자만, 이미 준 1회성 포인트는 숨김)은 데스크탑과 같다.
     */
    blockActs(key) {
        const lv    = +((typeof G5 !== 'undefined' && G5.admLevel) || 0);
        const isCar = ((this.curSum || {}).driverType || '') === '수발주자';
        const d     = this.curDetail || {};
        const given = d.points || {};
        const B = (act, label, cls = '') => `<button type="button" class="ma-ab-btn ${cls}" data-mact="${act}">${label}</button>`;

        // 아이디 변경은 변경이력이 함께 남는 별도 처리라 [저장] 통로를 타지 않는다
        if (key === 'basic') return B('pwd-reset', '비밀번호 리셋') + B('id-change', '아이디 변경');
        if (key === 'fee') {
            return isCar ? B('fee-set', '회비 설정', 'primary') + B('fee-log', '회비 내역') : '';
        }
        if (key === 'bank') {
            // 계좌는 소유자 귀속(g5_company)이다. 소속자에게 버튼을 내면 눌러 저장해도 서버가
            // 조용히 무시한다(MemberCompanyService::update 가 소유자만 통과) — 안 내는 게 맞다.
            if (((this.curSum || {}).permit || '') !== '소유자') return '';
            const f = d.fee || {};
            return B('bank1', '출금계좌 입력', 'primary') + B('bank2', 'CMS계좌 입력')
                + (this.bankShow(2) ? B('copy-1', 'CMS→출금 복사') : '')
                + (this.bankShow(1) ? B('copy-2', '출금→CMS 복사') : '')
                + (f.virtual ? '' : B('vacct', '가상계좌 발급'));
        }
        if (key === 'cash') {
            return B('cash-inout', '입출금', 'primary') + B('cash-log', '캐시 내역')
                + (lv >= 7 ? B('cash-recalc', '캐시 재계산') : '');
        }
        if (key === 'res') {
            return (isCar ? B('odr-set', '발주개수 설정', 'primary') + B('odr-log', '발주개수 내역') + B('msix-log', '-6차 내역') : '')
                + B('point-log', '수주포인트 내역')
                + (given.payat   ? '' : B('give-payat', '페이앳 200점'))
                + (given.sticker ? '' : B('give-sticker', '스티커 500점'))
                + (lv >= 7 ? B('point-recalc', '포인트 재계산') : '');
        }
        return '';
    }

    /** 버튼줄 분기 — 창을 열거나(내역·설정) 바로 처리한다(복사·재계산·지급) */
    onMact(act) {
        switch (act) {
            case 'find-addr':    return this.findAddress();
            case 'pwd-reset':    return this.pwdReset();
            case 'id-change':    return this.idChangeModal();
            case 'fee-set':      return this.prepayAddSheet();
            case 'fee-log':      return this.prepayLogSheet();
            case 'bank1':        return this.bankEdit(1);
            case 'bank2':        return this.bankEdit(2);
            case 'copy-1':       return this.bankCopy(1);
            case 'copy-2':       return this.bankCopy(2);
            case 'vacct':        return this.assignVirtual();
            case 'cash-inout':   return this.cashInoutSheet();
            case 'cash-log':     return this.ledger('cash');
            case 'cash-recalc':  return this.recalc('/ma/ajax/cash/recalc', '캐시');
            case 'odr-set':      return this.baljooCntSheet();
            case 'odr-log':      return this.ledger('odrcnt');
            case 'msix-log':     return this.ledger('msix');
            case 'point-log':    return this.ledger('points');
            case 'point-recalc': return this.recalc('/ma/ajax/point/recalc', '포인트');
            case 'penalty':      return this.penaltySheet();
            case 'give-payat':   return this.givePoint(200, 14, '페이앳 신청 200점');
            case 'give-sticker': return this.givePoint(500, 15, '스티커 인증샷 500점');
        }
    }

    // ───────── 계좌 ─────────

    /**
     * 계좌 입력 — 은행·계좌번호·예금주 세 칸. 데스크탑은 [저장] 때 함께 보내지만 여기서는
     * 확인 즉시 저장한다 (블록마다 따로 저장하는 화면이라 '나중에 저장'할 자리가 없다).
     * 출금계좌(1)는 출금대기 중이면 못 바꾼다 — 데스크탑과 같은 가드.
     */
    bankEdit(n) {
        if (n !== 1) return this.bankPrompt(n);
        $.getJSON('/ma/ajax/account/exist', { mb_no: this.curNum }, (r) => {
            if (r && r.result === true) return this.toast('출금대기중에는 계좌변경이 안 됩니다');
            this.bankPrompt(n);
        });
    }

    bankPrompt(n) {
        // 표시형('국민 , 123 , 홍길동')을 되돌려 칸을 채운다 — 원본은 파이프(|) 구분이다.
        // 이미 올려둔 값(스테이징)이 있으면 그쪽이 지금 화면의 값이다.
        const cur = this.bankShow(n).split(' , ');
        const in3 = (id, ph, v) => `<input class="ma-mdl-in" id="${id}" placeholder="${ph}" value="${this.esc(v || '')}">`;
        this.M.modal({
            title: n === 2 ? 'CMS계좌 입력' : '출금계좌 입력', okLabel: '저장',
            bodyHtml: '<label class="ma-mdl-lb">은행</label>' + in3('maBkBank', '은행명', cur[0])
                + '<label class="ma-mdl-lb">계좌번호</label>' + in3('maBkNo', '숫자만', cur[1])
                + '<label class="ma-mdl-lb">예금주</label>' + in3('maBkOwner', '예금주', cur[2]),
            onOk: (b, close) => {
                const v = ['maBkBank', 'maBkNo', 'maBkOwner'].map((id) => (b.querySelector(`#${id}`).value || '').trim());
                const str = v.some(Boolean) ? v.join('|') : '';
                close();
                this.stage(`co_bank${n}`, str);
            },
        });
    }

    /**
     * 출금↔CMS 복사 — dest 쪽에 반대편 값을 그대로 올린다.
     * 화면에만 반영하고 [저장] 에서 나간다 (되돌리려면 [취소]).
     */
    bankCopy(dest) {
        const src = dest === 1 ? 2 : 1;
        const str = this.bankShow(src).split(' , ').join('|');
        if (!str) return this.toast('복사할 계좌가 없습니다');
        this.stage(`co_bank${dest}`, str);
        this.toast(dest === 1 ? 'CMS→출금 복사 · 저장을 눌러주세요' : '출금→CMS 복사 · 저장을 눌러주세요');
    }

    /** 지금 화면에 보이는 계좌 표시값 — 올려둔 값이 있으면 그쪽이 먼저다 */
    bankShow(n) {
        const st = (this.staged || {})[`co_bank${n}`];
        if (st != null) return String(st).split('|').join(' , ');
        return String(((this.curDetail || {}).fee || {})[`bank${n}`] || '');
    }

    /**
     * 값을 화면에만 올려둔다 — 실제 전송은 하단 [저장].
     * 다시 그려야 카드의 값과 버튼(복사 원본 유무)이 올려둔 값을 따라간다.
     */
    stage(name, value) {
        this.staged = this.staged || {};
        this.staged[name] = value;
        this.renderTab();
    }

    /** 가상계좌 발급 — 이미 있으면 버튼 자체가 안 뜬다 */
    assignVirtual() {
        this.M.modal({
            title: '가상계좌 발급', okLabel: '발급',
            bodyHtml: '<p class="ma-mdl-desc">이 회원에게 입금용 가상계좌를 발급합니다.</p>',
            onOk: (b, close) => {
                $.post('/ma/ajax/account/assignBulk', { mb_no: this.curNum })
                    .done(() => { close(); this.toast('가상계좌가 발급되었습니다'); this.reopen(); })
                    .fail(() => this.toast('발급에 실패했습니다'));
            },
        });
    }

    // ───────── 제재 ─────────

    /**
     * 제재 관리 창 — 이력·부과·해제·삭제·수정 (penaltySheet.js).
     *
     * 목록·선택지는 상세 응답에 이미 실려 있어(MemberEditFormService::sanctions) 그대로 넘긴다.
     * 원장이 바뀌면 상세를 다시 받는다 — 「제재」 블록 요약과 목록 카드의 딱지가 함께 따라와야 한다.
     */
    penaltySheet() {
        openPenalty({
            mbNo: this.curNum,
            name: (this.curSum || {}).name || '',
            sanc: (this.curSum || {}).sanctions || null,
            onChange: () => this.reopen(),
        });
    }

    // ───────── 원장 내역 ─────────

    /**
     * 내역 창 — 캐시·발주개수·수주포인트·-6차 네 원장을 한 틀로 그린다.
     * 데스크탑은 5열 표지만 폰에서는 한 건을 두 줄(일시·사유 / 증감·잔액)로 접는다.
     * 페이지는 서버가 30건씩 자른다(logPage). -6차만 200건 한 번에 준다.
     */
    ledger(kind) {
        const C = LEDGER[kind];
        this.M.modal({
            title: C.title, hideOk: true,
            bodyHtml: '<div class="ma-lg" id="maLgBox"><div class="ma-sh-note">불러오는 중…</div></div>',
        });
        this.lgRows = '';
        this.ledgerLoad(kind, 1);
    }

    ledgerLoad(kind, page) {
        const C = LEDGER[kind];
        $.getJSON(this.url(this.curNum, C.seg), { page })
            .done((d) => {
                const box = document.getElementById('maLgBox');
                if (!box) return;
                d = d || {};
                const list = d.list || [];
                this.lgRows += list.map((r) => C.row(r, this)).join('');
                const more = !C.nopage && d.pages && page < d.pages;
                box.innerHTML = `<p class="ma-lg-head">${C.head(d, this)}</p>`
                    + (this.lgRows || '<div class="ma-sh-note">내역이 없습니다</div>')
                    + (more ? '<button type="button" class="ma-ab-btn wide" id="maLgMore">더보기</button>' : '');
                const btn = box.querySelector('#maLgMore');
                if (btn) btn.onclick = () => { btn.textContent = '불러오는 중…'; this.ledgerLoad(kind, page + 1); };
            })
            .fail(() => {
                const box = document.getElementById('maLgBox');
                if (box) box.innerHTML = '<div class="ma-sh-note">불러오지 못했습니다</div>';
            });
    }

    /** 원장 한 줄 — 증감은 부호로 색을 가른다 (내려간 값은 붉게) */
    lgRow(date, label, memo, diff, total, unit) {
        const sign = diff > 0 ? '+' : '';
        return `<div class="ma-lg-row">
            <div class="ma-lg-l"><b>${this.esc(label)}</b>${memo ? `<span>${this.esc(memo)}</span>` : ''}
                <time>${this.esc(date)}</time></div>
            <div class="ma-lg-r"><b class="${diff < 0 ? 'minus' : 'plus'}">${sign}${this.num(diff)}</b>
                <span>${this.num(total)}${unit}</span></div></div>`;
    }

    // ───────── 재계산 · 포인트 지급 ─────────

    /**
     * 원장 재계산 — 내역을 처음부터 다시 더해 보유액을 맞춘다 (level 7 이상).
     * 끝나고 상세를 다시 받는다 — 데스크탑은 편집폼이라 새로고침을 피했지만 여기는 블록 단위라
     * 입력 중인 칸이 없다.
     */
    recalc(url, what) {
        this.M.modal({
            title: `${what} 재계산`, okLabel: '재계산',
            bodyHtml: `<p class="ma-mdl-desc">내역(원장)을 처음부터 다시 더해 보유 ${what}를 맞춥니다.\n합계와 보유액이 어긋나 보일 때만 쓰세요.</p>`,
            onOk: (b, close) => {
                $.post(url, { mb_no: this.curNum })
                    .done(() => { close(); this.toast('처리했습니다'); this.reopen(); })
                    .fail(() => this.toast('처리에 실패했습니다'));
            },
        });
    }

    /** 1회성 포인트 지급 (페이앳·스티커) — 회원당 한 번뿐이라 서버도 중복을 막는다 */
    givePoint(pt, typenum, label) {
        this.M.modal({
            title: '포인트 지급', okLabel: '지급',
            bodyHtml: `<p class="ma-mdl-desc">${this.esc(label)}을 지급합니다. 회원당 한 번만 나갑니다.</p>`,
            onOk: (b, close) => {
                $.post(this.url(this.curNum, 'point'), { togoPoint: pt, typenum, chmemno: 0 })
                    .done((res) => {
                        const r = (res && typeof res === 'object') ? res : {};
                        if (r.result === false) return this.toast(r.message || '지급하지 못했습니다');
                        close(); this.toast('지급했습니다'); this.reopen();
                    })
                    .fail(() => this.toast('지급에 실패했습니다'));
            },
        });
    }

    // ───────── 발주개수 · 회비 ─────────

    /** 발주개수 설정 — 데스크탑 모달과 같은 폼(현재값·계산값을 보여주고 새 값을 받는다) */
    baljooCntSheet() {
        $.getJSON('/ma/ajax/member/baljoo/cnt/form', { no: this.curNum }).done((d) => {
            d = d || {};
            this.M.modal({
                title: '발주개수 설정', okLabel: '적용',
                bodyHtml: `<div class="ma-md-grid">
                        ${this.kv('현재 발주개수', `${this.num(d.currentCnt)}개`)}
                        ${this.kv('이번달 수주', `${this.num(d.thisMonthSooCnt)}개`)}
                        ${this.kv('계산된 발주개수', `${this.num(d.baljooCnt)}개`)}
                        ${this.kv('총 수주개수', `${this.num(d.soojoo)}개`)}
                    </div>
                    <label class="ma-mdl-lb">변경할 발주개수</label>
                    <input class="ma-mdl-in" id="maOdrNew" type="tel" inputmode="numeric" value="${+d.currentCnt || 0}">`,
                onOk: (b, close) => {
                    const v = parseInt((b.querySelector('#maOdrNew').value || '').replace(/[^\d-]/g, ''), 10);
                    if (isNaN(v)) return this.toast('숫자만 입력해주세요');
                    $.post('/ma/ajax/member/baljoo/cnt/save', { no: this.curNum, newvalue: v })
                        .done((res) => {
                            const r = (res && typeof res === 'object') ? res : {};
                            if (!r.result) return this.toast(r.message || '저장하지 못했습니다');
                            close(); this.toast(r.message || '저장했습니다'); this.reopen();
                        })
                        .fail(() => this.toast('저장에 실패했습니다'));
                },
            });
        }).fail(() => this.toast('불러오지 못했습니다'));
    }

    /** 회비 장기선결제 추가 — 기간을 고르면 시작·종료 년월과 금액이 정해진다 */
    prepayAddSheet() {
        $.getJSON('/ma/ajax/member/prepay/form', { mb_no: this.curNum }).done((d) => {
            d = d || {};
            if (d.result === false) return this.toast(d.message || '불러오지 못했습니다');
            const price = d.prepayPrice || {};
            const label = (p) => (+p === 12 ? '1년' : +p === 24 ? '2년' : +p === 36 ? '3년' : `${p}개월`);
            const opts = Object.entries(d.periods || {}).map(([p, endYm]) =>
                `<option value="${p}" data-end="${endYm}">${label(p)} · ${this.num(price[p] || 0)}원 (${d.startYm} ~ ${endYm})</option>`).join('');
            this.M.modal({
                title: '회비 장기선결제', okLabel: '추가',
                bodyHtml: `<div class="ma-md-grid">${this.kv('CMS', d.cmsnum || '설정안됨')}</div>
                    <label class="ma-mdl-lb">기간</label>
                    <select class="ma-mdl-in" id="maPpPeriod">${opts}</select>`,
                onOk: (b, close) => {
                    const sel = b.querySelector('#maPpPeriod');
                    const opt = sel.options[sel.selectedIndex];
                    if (!opt) return this.toast('기간을 선택해주세요');
                    $.post('/ma/ajax/member/prepay/add', {
                        mb_no: this.curNum, period: sel.value, start_ym: d.startYm, end_ym: opt.dataset.end,
                    }).done((res) => {
                        const r = (res && typeof res === 'object') ? res : {};
                        if (!r.result) return this.toast(r.message || '추가하지 못했습니다');
                        close(); this.toast('추가했습니다'); this.reopen();
                    }).fail(() => this.toast('추가에 실패했습니다'));
                },
            });
        }).fail(() => this.toast('불러오지 못했습니다'));
    }

    /** 회비 내역 — 해지(환불)까지 여기서 한다. 해지된 건은 환불액이 0 이다 */
    prepayLogSheet() {
        this.M.modal({ title: '회비 내역', hideOk: true,
            bodyHtml: '<div class="ma-lg" id="maLgBox"><div class="ma-sh-note">불러오는 중…</div></div>' });

        $.getJSON('/ma/ajax/member/prepay/log', { mb_no: this.curNum }).done((d) => {
            const box = document.getElementById('maLgBox');
            if (!box) return;
            const list = (d && d.list) || [];
            box.innerHTML = list.length ? list.map((r) => `<div class="ma-lg-row">
                    <div class="ma-lg-l"><b>${this.esc(r.start_date)} ~ ${this.esc(r.end_date)}</b>
                        <span>${this.esc(r.period)}개월${+r.is_cur ? ` · 경과 ${this.num(r.used_days)}일` : ''}${r.cancel_date ? ` · 해지 ${this.esc(r.cancel_date)}` : ''}</span>
                        <time>${this.esc(r.datetime || '')}</time></div>
                    <div class="ma-lg-r">${+r.is_cur ? '<b class="plus">이용중</b>' : ''}
                        ${(!r.cancel_date && +r.refund_price > 0)
                            ? `<button type="button" class="ma-ab-btn danger" data-ppcancel="${r.num}" data-refund="${r.refund_price}">해지 ${this.num(r.refund_price)}원</button>`
                            : `<span>${r.cancel_date ? '해지됨' : ''}</span>`}
                    </div></div>`).join('')
                : '<div class="ma-sh-note">내역이 없습니다</div>';

            box.querySelectorAll('[data-ppcancel]').forEach((b) => {
                b.onclick = () => {
                    b.disabled = true;
                    $.post('/ma/ajax/member/prepay/refund', { mb_no: this.curNum, num: b.dataset.ppcancel })
                        .done((res) => {
                            const r = (res && typeof res === 'object') ? res : {};
                            if (!r.result) { b.disabled = false; return this.toast(r.message || '해지하지 못했습니다'); }
                            this.toast('해지했습니다');
                            this.prepayLogSheet();
                            this.reopen();
                        })
                        .fail(() => { b.disabled = false; this.toast('해지에 실패했습니다'); });
                };
            });
        }).fail(() => this.toast('불러오지 못했습니다'));
    }

    // ───────── 캐시 입출금 ─────────

    /**
     * 캐시 수동 입출금 — 데스크탑 별창(/adm/member/cash/inout)을 폰 폼으로 옮겼다.
     * 사유 자동완성(CASH_PRESET)·회비 년월 규칙까지 그대로다. 서버는 같은 엔드포인트를 쓴다.
     * 회비 계열 사유는 memos 가 'YYYY년MM월' 라벨이고 담당자 메모는 exmemos 로 간다 (레거시 규약).
     */
    cashInoutSheet() {
        const name = (this.curSum || {}).name || '';
        const have = +((this.curDetail || {}).res || {}).cash || 0;
        const reasons = Object.keys(CASH_PRESET_LIST).map((r) => `<option value="${r}">${r}</option>`).join('');
        const now = new Date();
        const years = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1]
            .map((y) => `<option value="${y}"${y === now.getFullYear() ? ' selected' : ''}>${y}</option>`).join('');
        const months = Array.from({ length: 12 }, (_, i) => i + 1)
            .map((m) => `<option value="${String(m).padStart(2, '0')}"${m === now.getMonth() + 1 ? ' selected' : ''}>${String(m).padStart(2, '0')}</option>`).join('');

        const m = this.M.modal({
            title: '캐시 입출금', okLabel: '적용',
            bodyHtml: `<div class="ma-md-grid">${this.kv('현재 보유', `${this.num(have)}원`)}</div>
                <label class="ma-mdl-lb">사유</label>
                <select class="ma-mdl-in" id="maCiReason">${reasons}</select>
                <div id="maCiFee" hidden>
                    <label class="ma-mdl-lb">회비 년월</label>
                    <span class="ma-ci-ym"><select class="ma-mdl-in" id="maCiYear">${years}</select>
                    <select class="ma-mdl-in" id="maCiMonth">${months}</select></span>
                </div>
                <label class="ma-mdl-lb">입출</label>
                <select class="ma-mdl-in" id="maCiInout"><option value="입금">입금</option><option value="출금">출금</option></select>
                <label class="ma-mdl-lb">금액</label>
                <input class="ma-mdl-in" id="maCiAmount" type="tel" data-amount-kor inputmode="numeric" placeholder="숫자만">
                <label class="ma-mdl-lb">메모</label>
                <textarea class="ma-mdl-ta" id="maCiMemo" placeholder="메모"></textarea>
                <p class="ma-mdl-desc" id="maCiAfter"></p>`,
            onOk: (b, close) => this.cashInoutSubmit(b, close),
        });
        this.cashInoutBind(m.bodyEl, name, have);
    }

    /** 사유를 고르면 입출·금액·메모를 채워준다 (담당자가 그대로 고쳐 쓸 수 있다) */
    cashInoutBind(b, name, have) {
        const $r = b.querySelector('#maCiReason');
        const $io = b.querySelector('#maCiInout');
        const $amt = b.querySelector('#maCiAmount');
        const $memo = b.querySelector('#maCiMemo');
        const $fee = b.querySelector('#maCiFee');
        const $after = b.querySelector('#maCiAfter');

        const calc = () => {
            const v = parseInt(String($amt.value || '').replace(/[^\d]/g, ''), 10) || 0;
            const after = $io.value === '출금' ? have - v : have + v;
            $after.textContent = `처리 후 보유 ${this.num(after)}원`
                + ($io.value === '출금' && after < 0 ? ' · 보유액보다 큽니다' : '');
        };
        const apply = () => {
            const p = CASH_PRESET_LIST[$r.value] || CASH_PRESET_NONE;
            $io.value = p.inorout;
            $amt.value = p.amount === '' ? '' : String(p.amount);
            $memo.value = String(p.memo || '').replace('{name}', name);
            $fee.hidden = !p.fee;
            calc();
        };
        $r.onchange = apply;
        $io.onchange = calc;
        $amt.oninput = calc;
        apply();
    }

    cashInoutSubmit(b, close) {
        const reason = b.querySelector('#maCiReason').value;
        const inorout = b.querySelector('#maCiInout').value;
        const amount = parseInt(String(b.querySelector('#maCiAmount').value || '').replace(/[^\d]/g, ''), 10);
        if (isNaN(amount) || amount < 0) return this.toast('금액을 입력해주세요');

        const p = CASH_PRESET_LIST[reason] || CASH_PRESET_NONE;
        const memo = (b.querySelector('#maCiMemo').value || '').trim();
        // 회비 계열은 memos 에 'YYYY년MM월' 라벨이 들어가고 담당자 메모는 exmemos 로 간다.
        // 서버가 이 라벨에서 회비 귀속월(rel_id)을 뽑는다 — 형식을 바꾸면 정산이 어긋난다.
        const ym = p.fee ? `${b.querySelector('#maCiYear').value}년${b.querySelector('#maCiMonth').value}월` : '';

        $.post('/ma/ajax/member/cash/transaction', {
            user_number: this.curNum, amountof: amount, inorout, reason,
            memos: p.fee ? ym : memo, exmemos: p.fee ? memo : '',
        }).done((res) => {
            const r = (res && typeof res === 'object') ? res : {};
            if (!r.result) return this.toast(r.message || '처리하지 못했습니다');
            close(); this.toast('적용되었습니다'); this.reopen();
        }).fail(() => this.toast('처리에 실패했습니다'));
    }

    /**
     * 편집 중인 블록의 **입력칸 위**에 붙는 것 — 지금은 「보유장비」뿐이다.
     *
     * 차량번호가 맨 위에 서고 그 아래 기종 선택(카셀렉트)이 온다. 매칭반경·GPS·차고지는
     * 그 뒤라, 위에서 아래로 '어느 장비인지 → 어떻게 매칭할지' 순으로 읽힌다.
     *
     * 다른 장비는 **보여주기만** 한다. 연결·해제·삭제·복구는 소유자의 장비 판을 통째로
     * 흔드는 일이라 데스크탑(장비목록 창)에 둔다. 여기서 고치는 것은 이 기사에 연결된 한 대뿐이다.
     */
    editTop(key) {
        if (key !== 'car') return '';
        if (this.carList == null) return '<div class="ma-sh-note">장비 불러오는 중…</div>';

        const mine = (this.carList || []).find((c) => +c.mb_no === +this.curNum);
        const others = (this.carList || []).filter((c) => +c.mb_no !== +this.curNum);

        // 카셀렉트가 그릴 자리 — 셀렉트/인풋은 회원 발주화면과 같은 모듈($j.F.CarSelect)이 채운다
        const editor = mine
            ? `<div class="ma-carsel">
                    <div class="ma-carsel-no">${this.esc(mine.car_platenumber || '번호판 없음')}
                        <span>${this.esc(mine.car_str || mine.car_type || '')}</span></div>
                    <select class="ma-mdl-in" id="maCarType"></select>
                    <select class="ma-mdl-in" id="maCarD1"></select>
                    <select class="ma-mdl-in" id="maCarD2"></select>
                    <select class="ma-mdl-in" id="maCarJungo"></select>
                    <input class="ma-mdl-in" id="maCarNo" type="text" autocomplete="off" placeholder="차량번호">
                    <div id="maCarOpt"></div>
                </div>`
            : '<div class="ma-sh-note">이 기사에 연결된 장비가 없습니다</div>';

        const rest = others.length
            ? '<div class="ma-cars"><p class="ma-lg-head">소유자의 다른 장비</p>' + others.map((c) => `
                <div class="ma-car"><div class="ma-car-l"><b>${this.esc(c.car_platenumber || '번호판 없음')}</b>
                    <span>${this.esc(c.car_str || c.car_type || '')}</span>
                    <em>${c.mem_name ? `${this.esc(c.mem_name)} 배정` : '미배정'}</em></div></div>`).join('') + '</div>'
            : '';

        return editor + rest;
    }

    /**
     * 비밀번호 리셋 — **핸드폰 뒤 4자리로 초기화**한다. 임의의 비밀번호를 CS 가 타이핑하는
     * 입력칸은 두지 않는다 (데스크탑에는 있지만, 폰에서 실제로 쓰이는 건 리셋 하나다).
     *
     * 누르는 즉시 나가지 않고 올려둔다 — 잘못 눌렀을 때 [취소] 로 되돌릴 수 있어야 한다.
     */
    pwdReset() {
        const tail = String(((this.curDetail || {}).basic || {}).phone || (this.curSum || {}).phone || '').replace(/[^\d]/g, '');
        if (tail.length < 4) return this.toast('핸드폰 번호가 없습니다');
        this.stage('mem_pwd', tail.slice(-4));
        this.stage('pwd_reset', 1);     // 리셋이라 회원이 다음 접속 때 새로 정한다 (Admin\MemberController::save)
        this.toast('핸드폰 뒤 4자리로 초기화 · 저장을 눌러주세요');
    }

    /**
     * 아이디 변경 — 아이디는 핸드폰번호다. 중복확인을 통과한 번호만 바꿀 수 있고
     * 변경이력이 함께 남는다(서버). 그래서 기사정보 [저장] 과 통로가 다르다.
     */
    idChangeModal() {
        const m = this.M.modal({
            title: '아이디 변경', okLabel: '변경',
            bodyHtml: `<div class="ma-md-grid">${this.kv('현재 아이디', ((this.curDetail || {}).basic || {}).memId || '-')}</div>
                <label class="ma-mdl-lb">새 아이디 (핸드폰번호)</label>
                <input class="ma-mdl-in" id="maIdNew" type="tel" inputmode="numeric" placeholder="숫자만">
                <p class="ma-mdl-desc" id="maIdMsg">번호를 넣고 [중복확인] 을 누르세요.</p>
                <button type="button" class="ma-ab-btn wide" id="maIdCheck">중복확인</button>`,
            onOk: (b, close) => {
                if (!this._idOk || this._idOk.length < 10) return this.toast('중복확인 후 눌러주세요');
                $.post(this.url(this.curNum, 'id'), { changenum: this._idOk })
                    .done((res) => {
                        const r = (res && typeof res === 'object') ? res : {};
                        if (!r.result) return this.toast(r.message || '변경하지 못했습니다');
                        this._idOk = '';
                        close();
                        this.toast('아이디를 변경했습니다');
                        this.reopen();
                    })
                    .fail(() => this.toast('변경에 실패했습니다'));
            },
        });

        this._idOk = '';
        const b = m.bodyEl;
        const msg = b.querySelector('#maIdMsg');
        b.querySelector('#maIdNew').oninput = () => { this._idOk = ''; msg.textContent = '번호를 넣고 [중복확인] 을 누르세요.'; };
        b.querySelector('#maIdCheck').onclick = () => {
            const v = (b.querySelector('#maIdNew').value || '').replace(/[^\d]/g, '');
            if (v.length < 10) return this.toast('전화번호를 입력해주세요');
            $.post('/ma/ajax/member/id/check', { changenum: v })
                .done((d) => {
                    if (d && d.exists) { this._idOk = ''; msg.textContent = '이미 사용중인 아이디입니다.'; }
                    else { this._idOk = v; msg.textContent = '변경 가능합니다. [변경] 을 누르세요.'; }
                })
                .fail(() => this.toast('확인하지 못했습니다'));
        };
    }

    /**
     * 대표소속 3단 선택 붙이기 — 채우기는 common.js 공용 헬퍼(/ajax/sosok)가 한다.
     * 접수화면 상세검색(Order.bindSosok)과 같은 통로라 소속 목록이 두 화면에서 갈리지 않는다.
     *
     * 보내는 값은 **가장 하위 선택**이다 (센터 → 없으면 지사 → 없으면 본사).
     */
    mountSosok() {
        if (!document.getElementById('maSsB')) return;
        const cur = String((this.curSum || {}).sosokCode || '');
        const sync = () => {
            const el = document.getElementById('maEd_sosok');
            if (el) el.value = $('#maSsC').val() || $('#maSsG').val() || $('#maSsB').val() || '';
        };
        $j.Common.Form.updateBonsaSelectBox(cur, 'maSsB', 'maSsG', 'maSsC', sync);
        $j.Common.Form.registerBonsaHandler('maSsB', 'maSsG', 'maSsC', sync);
        $j.Common.Form.registerGisaHandler('maSsG', 'maSsC');
        $('#maSsB, #maSsG, #maSsC').on('change', sync);
    }

    /** 소유자의 장비 목록 — 「보유장비」 편집을 켤 때 한 번 받는다 */
    loadCars() {
        const owner = +((this.curDetail || {}).sosok || {}).ownerNo || this.curNum;
        this.carList = null;
        $.post(`/ma/ajax/${owner}/cars`, { mb_no: this.curNum })
            .done((res) => { this.carList = (res && res.data) || []; })
            .fail(() => { this.carList = []; })
            .always(() => {
                if (this.editing !== 'car') return;
                this.renderTab();
                // 목록·카셀렉트가 붙으며 높이가 늘었다 — 켤 때 맞춰둔 스크롤을 다시 잡는다
                const box = this.sheetEl && this.sheetEl.querySelector('#maMdBody');
                if (box) reScrollSecTop(box.querySelector('[data-sec="car"]'), this.sheetEl.querySelector('.ma-tabs'));
            });
    }

    /** 카셀렉트 붙이기 — 셀렉트가 DOM 에 선 뒤라야 한다 (renderTab 끝에서 부른다) */
    mountCarSelect() {
        const mine = (this.carList || []).find((c) => +c.mb_no === +this.curNum);
        if (!mine || !document.getElementById('maCarType')) return;
        $j.F.CarSelect.initialize({
            idList: { car_type: 'maCarType', depth1: 'maCarD1', depth2: 'maCarD2',
                      jungo: 'maCarJungo', number: 'maCarNo', option: 'maCarOpt' },
            v: mine,
        });
    }

    /**
     * 장비 저장 — 기사정보(v2/save)와 **다른 테이블**이라 요청이 따로 나간다(CarController@apply).
     * 장비를 먼저 보내고, 성공했을 때만 기사정보를 이어 보낸다. 번호판 중복 같은 거부를 만나면
     * 화면을 그대로 둔 채 사유만 알린다 — 반쯤 저장된 상태로 카드를 닫지 않기 위해서다.
     *
     * @return {Promise|null} 보낼 장비가 없으면 null
     */
    saveCar() {
        const s = $j.F.CarSelect;
        if (!this.carList || !document.getElementById('maCarType') || !s) return null;
        const owner = +((this.curDetail || {}).sosok || {}).ownerNo || this.curNum;
        const data = {
            car_type: s.getCarType(), depth1: s.getDepth1(), depth2: s.getDepth2(),
            jungo: s.getJungo(), number: s.getNumber(), option: s.getOption(),
            mb_no: this.curNum,
        };
        if (!data.car_type || !data.depth1 || !data.depth2 || (data.car_type !== '리프트' && !data.jungo)) {
            return $.Deferred().reject({ message: '장비 필수항목을 모두 입력해주세요' }).promise();
        }
        return $.post(`/ma/ajax/${owner}/car/apply`, data).then((d) => (
            (d && d.result === true) ? d : $.Deferred().reject(d).promise()
        ));
    }

    /**
     * 차고지 주소 — 지도에서 고른다. 주소와 좌표를 함께 올려둬야(is_gps_update) 서버가
     * 차고지 GPS 까지 옮긴다. 주소만 바꾸면 좌표가 옛 자리에 남아 당일외 매칭이 어긋난다.
     */
    findAddress() {
        const cur = ((this.curDetail || {}).car || {}).gps || '';   // 지금 차고지 자리에서 지도를 연다
        window.callbackAddress = (arr) => {
            if (!arr) return;
            this.staged = this.staged || {};
            this.staged.car_parkaddress = arr.addr;
            this.staged.gps = `${arr.lat},${arr.long}`;
            this.staged.is_gps_update = 1;
            delete window.callbackAddress;
            this.renderTab();
        };
        $j.F.KakaoMap.open(cur, '0', '');
    }

    /** 섹션 하나 — 값이 빈 줄은 빼고, 남는 줄이 없으면 블록 자체를 안 그린다 */
    infoBlock(title, rows, raw = false, btn = '', tail = '', secKey = '') {
        const list = rows.filter((r) => r[1] != null && r[1] !== '');
        if (!list.length && !btn) return '';   // 고칠 수 있는 블록은 비어도 남긴다 (넣을 자리가 있어야 한다)
        const cell = raw
            ? (r) => `<div class="ma-kv"><span class="k">${this.esc(r[0])}</span><span class="v">${r[1]}</span></div>`
            : (r) => this.kv(r[0], r[1]);
        const h4 = btn ? `<h4 class="has-edit">${this.esc(title)}${btn}</h4>` : `<h4>${this.esc(title)}</h4>`;
        return `<div class="ma-sh-sec"${secKey ? ` data-sec="${secKey}"` : ''}>${h4}
            <div class="ma-md-grid">${list.map(cell).join('')}</div>${tail}</div>`;
    }

    onAct(el, act) {
        switch (act) {
            case 'standby':  return this.confirmModal({ title: '대기전환', desc: '이 기사를 대기 상태로 전환할까요?', method: 'post', seg: 'standby' });
            case 'rollback': return +(this.curSum || {}).status === 2
                ? this.confirmModal({ title: '휴면해제', desc: '이 회원의 휴면을 해제할까요? 해제되면 앱을 다시 켤 때부터 바로 쓸 수 있습니다.', method: 'post', seg: 'rollback' })
                : this.confirmModal({ title: '정상복구', desc: '이 기사를 정상(승인) 상태로 복구할까요?', method: 'post', seg: 'rollback' });
            case 'leave':    return this.confirmModal({ title: '탈퇴 처리', desc: '정말 탈퇴 처리할까요?\n소유자면 소속 기사도 함께 탈퇴됩니다. 되돌리기 어렵습니다.', method: 'delete', seg: '', okCls: 'danger', okLabel: '탈퇴' });
            case 'push-register': return this.push('register');
            case 'push-match':    return this.push('match');
            case 'order-test':    return this.confirmModal({
                title: '수주 테스트 발주',
                desc: '이 기사에게만 가는 어플오더를 만듭니다.\n내일 오전 9시 · 차고지 기준 · 전달사항 "ㅡ 수주테스트 ㅡ"\n실제 오더라 취소는 접수화면에서 해야 합니다.',
                method: 'post', seg: 'order-test', okLabel: '발주', keepSheet: true,
            });
            case 'counsel-add':   return this.counselAddModal();
            case 'docs':          return this.proxyEnter('/m/docs/v1');
            case 'proxy-bal':     return this.proxyEnter('/m/bal');
            case 'proxy-home':    return this.proxyEnter('/m');
        }
    }

    /**
     * 회원 계정으로 대리진입한다. 세션이 통째로 그 회원으로 바뀌므로 되돌릴 곳(from)을 함께 넘긴다.
     * 서버가 세션에 적어두고, 회원 화면의 복귀바($j.F.Mobile.ProxyBar)가 그걸로 돌아온다.
     */
    proxyEnter(returnUrl) {
        // 대리진입은 페이지를 통째로 넘긴다 — 고치던 칸이 있으면 말없이 사라진다
        if (this.editing && this.dirtyEdits()) return this.toast('저장 또는 취소 후 이동해주세요');

        // 복귀하면 목록이 아니라 **보던 기사 상세**로 되돌아온다 (?open= — openFromUrl 이 받는다).
        // 시트를 빌려 쓴 화면(불러톡 등)은 backUrl 로 자기 자리를 준다 (driverSheet.js).
        const back = this.backUrl || `/ma/members?open=${this.curNum}`;
        const q = `mb_no=${this.curNum}`
            + `&returnurl=${encodeURIComponent(returnUrl)}`
            + `&from=${encodeURIComponent(back)}`;
        location.replace(`/ma/proxy/enter?${q}`);
    }

    url(n, seg) { return seg ? `/ma/ajax/member/${n}/${seg}` : `/ma/ajax/member/${n}`; }

    get M() { return $j.Sites.MobileAdmin; }

    /**
     * @param {boolean} [keepSheet] 성공해도 시트를 닫지 않는다.
     *   상태변경·탈퇴는 그 회원을 더 볼 일이 없어 닫는 게 맞지만, 테스트 발송·토큰 리셋은
     *   결과를 확인하고 이어서 다른 것도 눌러보는 자리라 닫으면 매번 다시 찾아 들어와야 한다.
     */
    confirmModal({ title, desc, method, seg, okCls = 'primary', okLabel, keepSheet }) {
        this.M.modal({
            title, okLabel: okLabel || title, okCls,
            bodyHtml: `<p class="ma-mdl-desc">${this.esc(desc)}</p>`,
            onOk: (b, close) => (keepSheet
                ? this.actKeep(this.url(this.curNum, seg), method, close)
                : this.act(this.url(this.curNum, seg), method, {}, close)),
        });
    }

    /** 시트를 닫지 않는 액션 — 결과만 알리고 상세를 다시 받는다 */
    actKeep(url, method, closeModal) {
        $.ajax({ url, type: method })
            .done((res) => {
                const obj = (res && typeof res === 'object') ? res : {};
                const fail = obj.result === false;
                this.toast(obj.message || (fail ? '처리하지 못했습니다' : '처리되었습니다'));
                if (!fail) { closeModal && closeModal(); this.reopen(); }
            })
            .fail((xhr) => this.toast(xhr.status === 401 ? '세션이 만료되었습니다' : '요청에 실패했습니다'));
    }

    push(type) {
        $.ajax({ url: this.url(this.curNum, 'push-test'), type: 'post', data: { type } })
            .done((res) => this.toast((res && res.message) || '전송했습니다'))
            .fail(() => this.toast('전송에 실패했습니다'));
    }

    counselAddModal() { this.counselModal(null); }

    /** 줄의 [수정] — 원본은 목록 응답(cache.counselRows)에서 꺼낸다 */
    counselEditModal(num) {
        const row = (this.cache.counselRows || []).find((c) => +c.num === +num);
        if (!row) return this.toast('상담을 찾을 수 없습니다');
        this.counselModal(row);
    }

    /**
     * 상담 입력창 — 추가·수정이 같은 모양이다 (유형 + 내용).
     *
     * 상담일은 칸을 두지 않는다. 폰에서 고칠 일이 드문 데다 상담내역의 순서·경고 끼워넣기
     * 기준이라(CounselController::mergeWarnings) 잘못 만지면 줄 자리가 틀어진다 —
     * 수정 시에는 **원래 상담일을 그대로 되돌려 보낸다**. 날짜 교정은 PC 상담내역에서 한다.
     *
     * @param {object|null} row 수정할 상담줄. null 이면 추가
     */
    counselModal(row) {
        const isEdit = !!row;
        const cur = (row && row.type) || '상담';
        const types = Object.keys(CNS_BG);
        if (!types.includes(cur)) types.unshift(cur);   // 옛 유형이 저장돼 있으면 목록에 없어도 그대로 지킨다

        this.M.modal({
            title: isEdit ? '상담 수정' : '상담 추가', okLabel: '저장',
            bodyHtml: `<label class="ma-mdl-lb">유형</label>
                <select class="ma-mdl-in" id="maCnsType">
                    ${types.map((t) => `<option value="${this.esc(t)}"${t === cur ? ' selected' : ''}>${this.esc(t)}</option>`).join('')}
                </select>
                <label class="ma-mdl-lb">내용</label>
                <textarea class="ma-mdl-ta" id="maCnsContent" placeholder="상담 내용">${this.esc((row && row.content) || '')}</textarea>`,
            onOk: (b, close) => {
                const content = (b.querySelector('#maCnsContent').value || '').trim();
                if (!content) return this.toast('상담 내용을 입력해주세요');

                const data = { type: b.querySelector('#maCnsType').value, content };
                if (isEdit) {
                    data.num = row.num;
                    if (row.counsel_date) data.counsel_date = row.counsel_date;
                }
                $.ajax({
                    url: this.url(this.curNum, isEdit ? 'counsel/update' : 'counsel/add'),
                    type: 'post', data,
                }).done((res) => {
                    this.toast((res && res.message) || (isEdit ? '수정되었습니다' : '추가되었습니다'));
                    if (!res || res.result !== false) {
                        close();
                        // 방금 고친(넣은) 상담이 바로 보이게 목록을 다시 받는다
                        const box = this.sheetEl && this.sheetEl.querySelector('#maMdBody');
                        if (box && this.tab === 'counsel') this.renderCounsel(box, true);
                    }
                }).fail(() => this.toast(isEdit ? '상담 수정에 실패했습니다' : '상담 추가에 실패했습니다'));
            },
        });
    }

    /** 액션 요청 공통 처리 — 성공 시 모달·시트 닫고 목록 갱신 */
    act(url, method, data, closeModal) {
        $.ajax({ url, type: method, data })
            .done((res) => {
                const obj = (res && typeof res === 'object') ? res : {};
                const fail = obj.result === false;
                this.toast(obj.message || (fail ? '처리하지 못했습니다' : '처리되었습니다'));
                if (!fail) { closeModal && closeModal(); $j.Ui.BS.close(); this.reload(); }
            })
            .fail((xhr) => this.toast(xhr.status === 401 ? '세션이 만료되었습니다' : '요청에 실패했습니다'));
    }

    toast(msg) { if ($j.Ui && $j.Ui.Toast) $j.Ui.Toast.show(msg); }
    num(v) { return (parseInt(String(v).replace(/[^\d-]/g, ''), 10) || 0).toLocaleString('ko-KR'); }
    stripTags(s) { return String(s || '').replace(/<[^>]*>/g, '').trim(); }
    esc(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
}
